// pipeline-phases.ts — packshot/beskrivelse/tekst/model-faser, regenerering, runPipeline.
// AUTO: del af pipeline (splittet fra pipeline.ts). Klassisk script — deler global scope.
async function cleanWhiteBackground(img: ImageResult, _label?: string, _legacy?: boolean): Promise<ImageResult> {
    return img;
}

// Appends an optional free-text user instruction (typed in the regenerate
// popup) to a base prompt, without letting it override the core rules.
function appendUserInstruction(prompt: string, instruction?: string): string {
    const t = (instruction || '').trim();
    if (!t) return prompt;
    return prompt + '\n\nADDITIONAL USER INSTRUCTION — apply this on top of every rule above, but never break the garment fidelity, exact-color and pure-white-background rules:\n' + t;
}

// The user can upload close-up reference photos (neck tag + detail shot). Previously
// these were ONLY used for the text description (Phase 2) and were discarded for
// image generation — the single biggest detail-preservation gap. detailRefImgs()
// collects them so they can be passed as fidelity references, and the packshot and
// model prompts say in their list of images what each one is.
function detailRefImgs(ctx: any): File[] {
    const u = ctx?.uploads || {};
    // each knows what it is, for the packshot prompt's list of images
    if (u.necktag) (u.necktag as any).__closeup = 'necktag';
    if (u.detail) (u.detail as any).__closeup = 'detail';
    return [u.necktag, u.detail].filter((f: any): f is File => !!f);
}

// The brand's neck label (it comes with the brand chosen in Packshot) is a reference
// only where it belongs (2026-10-14, Simon: "hvis logoet er synligt for ai'en skal det
// ikke bruges ... den må ikke bare sætte det ind uden det passer"): for a garment with
// a neck, whose photo does not show its label so it can be read. A quick look by Gemini
// at the front photo decides, once for each photo. A neck label uploaded by hand is the
// garment's own, and always used.
// The same quick look says what kind of garment it is, for the packshot (2026-10-20: long
// underwear came out as the top of an invisible mannequin over the floor photo's legs,
// blurred into the white, when the packshot prompt only knew shoulders, sleeves and hems).
type GarmentKind = 'top' | 'bottom' | 'full' | 'other';
const LABEL_LOOK = 'Look at Image 1, a photo of a garment lying on a floor, and answer with JSON only, in this shape: {"kind": "top", "neck": true, "label": "none"}\n'
    + 'kind: "top" for a garment worn on the upper body (a T-shirt, shirt, polo, sweater, hoodie, cardigan, jacket, coat, vest or base-layer top); "bottom" for one worn on the lower body only (trousers, jeans, leggings, long underwear, tights, shorts or a skirt); "full" for one piece over both (a dress, jumpsuit, overall or onesie); "other" for anything else (socks, a cap, gloves or a bag).\n'
    + 'neck: true when the garment has a neckline or a collar, with a neck label sewn inside the back of it (a T-shirt, shirt, polo, sweater, hoodie, cardigan, jacket, coat or dress); false when it has none (trousers, shorts, a skirt, swimwear, socks, a cap or a bag).\n'
    + 'label: "clear" when the label sewn inside the back of the neck shows in the photo and its words can be read; "unclear" when it shows but its words cannot be read; "none" when it does not show.';
const __labelLook = new WeakMap<File, Promise<{ kind: GarmentKind; neck: boolean; label: string } | null>>();
function garmentLook(photo: File): Promise<{ kind: GarmentKind; neck: boolean; label: string } | null> {
    let look = __labelLook.get(photo);
    if (!look) {
        // one quick call for each model, no waiting and retrying: if it fails, the label is used as before
        look = (async () => {
            const parts = [{ inlineData: { mimeType: getMime(photo), data: await fileToBase64(photo) } }, { text: LABEL_LOOK }];
            for (const model of ['gemini-3.8-flash', 'gemini-3.1-pro-preview']) {
                try {
                    const r = await apiFetch(`${GEMINI_BASE}/${model}:generateContent`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ contents: [{ role: 'user', parts }], generationConfig: { temperature: 0, responseMimeType: 'application/json' } }),
                        signal: AbortSignal.timeout(30_000),
                    });
                    if (!r.ok) continue;
                    const j: any = await r.json();
                    const text = ((j.candidates?.[0]?.content?.parts || []) as { text?: string; thought?: boolean }[]).filter(x => x.text && !x.thought).map(x => x.text).join('');
                    const a = JSON.parse(text.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, ''));
                    const kind: GarmentKind = ['top', 'bottom', 'full', 'other'].includes(a.kind) ? a.kind : 'top';
                    return { kind, neck: a.neck !== false && kind !== 'bottom', label: ['clear', 'unclear', 'none'].includes(a.label) ? a.label : 'none' };
                } catch { /* the next model */ }
            }
            return null;
        })();
        __labelLook.set(photo, look);
    }
    return look;
}
// the back photo is the same garment as the front: it is told from the front's look
const __sameGarment = new WeakMap<File, File>();
function sameGarment(back: File | null | undefined, front: File | null | undefined) { if (back && front && back !== front) __sameGarment.set(back, front); }
// what kind of garment a floor photo shows; a top when it cannot be told, as before
async function garmentKind(photo: File | null | undefined, log?: (msg: string) => void): Promise<GarmentKind> {
    if (!photo) return 'top';
    const seen = await garmentLook(__sameGarment.get(photo) || photo);
    const kind = seen ? seen.kind : 'top';
    if (kind !== 'top') log?.('  The garment is ' + ({ bottom: 'worn on the lower body (trousers, leggings, shorts or a skirt)', full: 'one piece (a dress or a jumpsuit)', other: 'not a top or trousers' } as Record<string, string>)[kind]);
    return kind;
}
async function brandLabelUsed(ctx: any, log?: (msg: string) => void): Promise<boolean> {
    const tag: any = ctx?.uploads?.necktag, front: File | null = ctx?.uploads?.front || null;
    if (!tag) return false;
    if (!tag.__brandTag) return true;
    if (!front) return false;
    const seen = await garmentLook(front);
    const name = tag.__brandName ? tag.__brandName + '\'s neck label' : 'The neck label';
    if (!seen) { log?.('  ' + name + ': could not be checked, so it is used'); return true; }
    if (!seen.neck) { log?.('  ' + name + ': not used, the garment has no neck'); return false; }
    if (seen.label === 'clear') { log?.('  ' + name + ': not used, the photo shows the label so it can be read'); return false; }
    log?.('  ' + name + ': used, ' + (seen.label === 'unclear' ? 'the photo shows the label but it cannot be read' : 'the photo does not show the label'));
    return true;
}
// the close-ups for a picture of the garment: the brand's neck label only where it is used
async function closeupsFor(ctx: any, log?: (msg: string) => void): Promise<File[]> {
    const all = detailRefImgs(ctx);
    if (!ctx?.uploads?.necktag || await brandLabelUsed(ctx, log)) return all;
    return all.filter(f => (f as any).__closeup !== 'necktag');
}

// ── The packshot prompt ──
// One prompt (PACKSHOT in the prompt manager) for every packshot: front and back,
// invisible mannequin and flat-lay. Written from scratch on 2026-10-07 in place of
// five prompts and three guard blocks, some 70,000 characters that said the same
// things many times. Its fields are filled from what is chosen in Packshot: the
// style, the side, the images there are (the floor photo; for the back, the
// finished front packshot; the brand's neck label or an uploaded one; a detail),
// the background (White or Transparent) and the format.
interface PackshotPromptOpts {
    view: 'front' | 'back';
    flat: boolean;
    withFront: boolean;   // the back, with the finished front packshot as Image 1
    closeups: File[];     // the neck label and/or the detail, after the main image(s)
    aspect?: string;
    background?: string;  // 'white' | 'transparent'
    kind?: GarmentKind;   // a top unless the floor photo shows otherwise
}
const PACKSHOT_FORMATS: Record<string, string> = {
    '1:1': 'Square, 1:1', '4:5': 'Portrait, 4:5', '3:4': 'Portrait, 3:4', '9:16': 'Tall portrait, 9:16', '16:9': 'Landscape, 16:9',
};
const PACKSHOT_STYLE = {
    packshot: {
        name: 'an invisible-mannequin packshot',
        front: 'An invisible-mannequin (ghost mannequin) packshot, front view: the garment filled out as if worn by an invisible body. Rounded shoulders, an open neckline that stands in three dimensions, gentle volume over the chest, sleeves that hold their shape and hang slightly away from the body, and the hem curving softly over the hips, with soft light and shade following that form. The photos show the garment lying flat; the result does not look flat. Nothing holds it up and no body is drawn: no mannequin, neck block, stand, hanger, arm or hand, and past every sleeve opening and below the hem there is only white. In the neck opening you see the inside back of the garment.',
        back: 'An invisible-mannequin (ghost mannequin) packshot, back view: the garment filled out as if worn by an invisible body, seen from behind. Rounded shoulders, the back of the neckline standing in three dimensions, gentle volume over the shoulder blades, sleeves that hold their shape and hang slightly away from the body, and the hem curving softly over the hips, with soft light and shade following that form. The photos show the garment lying flat; the result does not look flat. Nothing holds it up and no body is drawn: no mannequin, neck block, stand, hanger, arm or hand, and past every sleeve opening, beside the body and below the hem there is only white.',
        check: 'Nothing holds the garment up and no body shows: no mannequin, stand or limb at the collar, through the sleeves, beside the body or below the hem.',
    },
    flat: {
        name: 'a flat-lay',
        front: 'A flat-lay, front view: the garment laid out completely flat and smooth and seen from straight above, as a cut-out: there is no table, paper or other surface under it, it casts no shadow, and the white around it is empty. The sleeves lie naturally along the sides of the body, the garment symmetrical and centred. No body form, no volume and no mannequin.',
        back: 'A flat-lay, back view: the garment laid out completely flat and smooth and seen from straight above, as a cut-out: there is no table, paper or other surface under it, it casts no shadow, and the white around it is empty. The sleeves lie naturally along the sides of the body, the garment symmetrical and centred. No body form, no volume and no mannequin.',
        check: 'The garment lies flat, seen from straight above, without body form, on empty white with no shadow under or around it.',
    },
};
// Trousers, leggings, long underwear, shorts and skirts: a waistband and legs, not shoulders and sleeves.
const PACKSHOT_STYLE_BOTTOM = {
    packshot: {
        front: 'An invisible-mannequin (ghost mannequin) packshot, front view: the garment filled out as if worn by an invisible body, from the waistband to the end of the legs. The waistband is a round opening that stands in three dimensions, with gentle volume over the hips, and the legs (or the skirt) are filled out into soft, rounded forms that hang straight down, slightly apart, with soft light and shade following that form all the way down to the leg openings or the hem. The photos show the garment lying flat; the result does not look flat anywhere, the lower legs included. Nothing holds it up and no body is drawn: no mannequin, stand, hanger, leg, foot or shoe. In the waist opening you see the inside back of the waistband, and below the leg openings or the hem there is only white.',
        back: 'An invisible-mannequin (ghost mannequin) packshot, back view: the garment filled out as if worn by an invisible body, seen from behind, from the waistband to the end of the legs. The back of the waistband stands in three dimensions, with gentle volume over the seat, and the legs (or the skirt) are filled out into soft, rounded forms that hang straight down, slightly apart, with soft light and shade following that form all the way down to the leg openings or the hem. The photos show the garment lying flat; the result does not look flat anywhere, the lower legs included. Nothing holds it up and no body is drawn: no mannequin, stand, hanger, leg, foot or shoe, and beside and below the garment there is only white.',
        check: 'The whole garment, from the waistband to the end of the legs or the hem, is one three-dimensional garment in one light: no part of it is flat, and no body, leg, foot or stand shows.',
    },
    flat: {
        front: 'A flat-lay, front view: the garment laid out completely flat and smooth and seen from straight above, as a cut-out: there is no table, paper or other surface under it, it casts no shadow, and the white around it is empty. The waistband lies straight and level, and the legs (or the skirt) lie straight down side by side, the garment symmetrical and centred. No body form, no volume and no mannequin.',
        back: 'A flat-lay, back view: the garment laid out completely flat and smooth and seen from straight above, as a cut-out: there is no table, paper or other surface under it, it casts no shadow, and the white around it is empty. The waistband lies straight and level, and the legs (or the skirt) lie straight down side by side, the garment symmetrical and centred. No body form, no volume and no mannequin.',
        check: 'The garment lies flat, seen from straight above, the legs straight and side by side, without body form, on empty white with no shadow under or around it.',
    },
};
// A bottom keeps every seam and stitch, and nothing of the floor photo. (A first wording,
// "every part of the garment is drawn anew", went to every garment; re-run on the same photo,
// it made EX10062's raglan, ribbed, waffle-knit top a plain smooth one, 4.5 against 10 for the
// prompt without it, so a top keeps its prompt as it was; 2026-10-20.)
const PACKSHOT_BOTTOM_KEEP = ' Every seam and stitch is where the photo has it and in its own colour: a light or contrasting stitch stays light, and the fabric keeps its own knit or rib. No part of the garment is left as it lies in the floor photo, flat or blurred, and none of the floor shows.';
const PACKSHOT_BOTTOM_CHECK = ' No part of it is blurred, faded or left as it lies in the floor photo, nothing of the floor shows, and every seam and stitch is as in the photo, in its own colour.';

function buildPackshotPrompt(o: PackshotPromptOpts): string {
    const tpl = String((PROMPTS as any).PACKSHOT || '').trim();
    if (!tpl) throw new Error('Packshot has no prompt yet');
    return fillPromptFields(tpl, packshotPromptFields(o));
}
// The fields of the PACKSHOT template for one picture: kept with the picture, so the prompt
// doctor (src/autofix.ts) can make it again with a changed template.
function packshotPromptFields(o: PackshotPromptOpts): Record<string, string> {
    const kind = o.kind || 'top';
    const style = kind === 'bottom'
        ? { name: o.flat ? PACKSHOT_STYLE.flat.name : PACKSHOT_STYLE.packshot.name, ...(o.flat ? PACKSHOT_STYLE_BOTTOM.flat : PACKSHOT_STYLE_BOTTOM.packshot) }
        : o.flat ? PACKSHOT_STYLE.flat : PACKSHOT_STYLE.packshot;
    // a dress or a jumpsuit: the body filled out down to its hem or its leg openings
    const full = (s: string) => kind === 'full' && !o.flat ? s.replace('and the hem curving softly over the hips', 'and the body filled out all the way down to the hem or the leg openings, which hang straight down') : s;
    const lines: string[] = [];
    if (o.view === 'front') {
        lines.push('Image 1: the front of the garment, photographed lying on a floor. It is the truth for everything on the front.');
    } else if (o.withFront) {
        lines.push('Image 1: the finished front of this garment. It sets the colour, the light and the size in the frame; the back matches it.');
        lines.push('Image 2: the back of the garment, photographed lying on a floor. It is the truth for everything on the back, but not for the colour: the light of the floor photo is not the garment\'s.');
        // the front's waistband tab came round to the back (re-run of EX30053, 2026-10-20)
        if (kind === 'bottom') lines.push('A tab, label or print on the front is not on the back unless Image 2 shows it there.');
    } else {
        lines.push('Image 1: the back of the garment, photographed lying on a floor. It is the truth for everything on the back. There is no image of the front: show only what this photo shows.');
    }
    let n = lines.length;
    let necktag: any = null;
    for (const f of o.closeups) {
        n++;
        const kind = (f as any).__closeup;
        if (kind === 'necktag') {
            necktag = f;
            const brand = (f as any).__brandName;
            lines.push('Image ' + n + ': ' + (brand ? 'a close-up of ' + brand + '\'s neck label, the label sewn inside the back of the collar of this garment.' : 'a close-up of this garment\'s neck label, sewn inside the back of the collar.'));
        } else {
            lines.push('Image ' + n + ': a close-up of a detail of this garment (a print, a logo or the fabric).');
        }
    }
    if (o.closeups.length) lines.push('The close-ups are references for those details only: they are not put into the picture, and the framing does not change to show them.');

    const colour = o.view === 'back' && o.withFront
        ? 'Colour: exactly the colour of the finished front (Image 1), in every part of the back.'
        : 'Colour: exactly the garment\'s colour in Image 1, judged where the fabric lies flat and in no shadow: not deeper, brighter, more saturated, cooler or warmer, and not tinted by the floor.';
    const rest = 'Where text is too small or blurred to read, that spot is plain fabric: no letter is ever guessed. A label, logo or print that the images do not show is not there.';
    const brand = necktag && necktag.__brandName;
    const labels = necktag
        ? 'Labels and text: the neck label is ' + (brand ? brand + '\'s label' : 'the label') + ' exactly as in its close-up, its size tab included, letter for letter, at the size and place it has in this garment, inside the back of the collar. '
            + 'It is in that one place only, seen through the neck opening at its real, small size: it is nowhere else, not on the outside of the collar or the back, the chest, a sleeve, the hem or a pocket, and the brand\'s name or logo is not added as a print, patch or label anywhere the garment photo does not show one. '
            + 'Every other letter, logo and print is copied from the images, letter for letter, at its size and place. ' + rest
        : 'Labels and text: every letter, logo, label and print is copied from the images, letter for letter, at its size and place. ' + rest;

    const fields: Record<string, string> = {
        STYLE_NAME: style.name,
        VIEW: o.view,
        IMAGES: lines.join('\n'),
        STYLE: full(o.view === 'front' ? style.front : style.back) + (kind === 'bottom' ? PACKSHOT_BOTTOM_KEEP : ''),
        COLOUR: colour,
        LABELS: labels,
        CUTOUT: o.background === 'transparent' ? ' The background is cut away for the webshop afterwards, so the white meets the garment\'s edge cleanly, without a grey halo.' : '',
        FORMAT: PACKSHOT_FORMATS[o.aspect || '1:1'] || PACKSHOT_FORMATS['1:1'],
        STYLE_CHECK: style.check + (kind === 'bottom' ? PACKSHOT_BOTTOM_CHECK : ''),
    };
    return fields;
}
const packshotChoices = () => ({ aspect: (state as any)?.billede?.outputAspect || '1:1', background: (state as any)?.billede?.packshotBackground || 'white' });

// ── The model prompt ──
// One prompt (MODEL in the prompt manager) for both model images, written from scratch
// on 2026-10-07 in place of six prompts, the code blocks appended to them and a second
// "refine" pass on every image. Its fields are filled from the choices in Packshot:
// the side, whether a model photo was uploaded, whether the packshots or only the
// floor photos are there, the detail close-up, the model background (White, Studio
// or As photo), the format and the brand (North Latitude: a big, tall man).
interface ModelPromptOpts {
    view: 'front' | 'back';
    person: 'upload' | 'generate';
    withFront: boolean;        // the back, turned from the finished front model image (Image 1)
    fromPackshot: boolean;     // the packshots are there (otherwise the floor photos stand in)
    closeups: File[];          // the detail close-up (the neck label does not show on a person)
    background: 'white' | 'studio' | 'photo';
    aspect?: string;
}
const MODEL_BG_RETRY = '\n\nThe last attempt was not on a light-grey studio backdrop. The background must be a seamless light-grey studio backdrop with a matching floor and one soft shadow under the shoes: no room, wall, corner or white cut-out.';
const packshotBrandId = () => String((window as any).__packshotBrandId || '');

function buildModelPrompt(o: ModelPromptOpts): string {
    const tpl = String((PROMPTS as any).MODEL || '').trim();
    if (!tpl) throw new Error('Packshot has no model prompt yet');
    return fillPromptFields(tpl, modelPromptFields(o));
}
// The fields of the MODEL template for one picture: kept with the picture, as the PACKSHOT ones,
// so the prompt doctor (src/autofix.ts) can make it again with a changed template.
function modelPromptFields(o: ModelPromptOpts): Record<string, string> {
    const front = o.view === 'front';
    const turned = !front && o.withFront;
    const upload = front && o.person === 'upload';
    const garment = front
        ? (o.fromPackshot ? 'the finished front packshot of the garment: the truth for the garment.' : 'the front of the garment, photographed lying on a floor; it stands in for the packshot: the truth for the garment.')
        : (o.fromPackshot ? 'the finished back packshot of the garment: the truth for the back of the garment.' : 'the back of the garment, photographed lying on a floor; it stands in for the packshot: the truth for the back of the garment.');
    const lines: string[] = [];
    if (upload) lines.push('Image 1: a photo of the model to dress.');
    if (turned) lines.push('Image 1: the finished model photo from the front: the person, the outfit, the light and the background.');
    lines.push('Image ' + (lines.length + 1) + ': ' + garment);
    let n = lines.length;
    for (const _f of o.closeups) { n++; lines.push('Image ' + n + ': a close-up of a detail of the garment (a print, a logo or the fabric), for that detail only: it is not put into the picture.'); }

    const someone = packshotBrandId() === 'north-latitude'
        ? 'A big, tall man with a full, strong build, as North Latitude\'s big-and-tall menswear (XL to 8XL) is made for: a real, natural-looking person, not a fitness model, standing relaxed with his arms loosely at his sides.'
        : 'A real, natural-looking adult fashion model who suits the garment\'s cut and size, standing relaxed with the arms loosely at the sides.';
    const person = upload
        ? 'The person in Image 1, exactly as they are: the same face and features, expression, skin, hair, facial hair, body and build, the same pose, hands and gaze. Not a similar person: this person. Only the garment they wear changes.'
        : turned
            ? 'The same person as in Image 1, seen from straight behind, as if the camera had walked around them: the same build, skin, hair and the rest of the outfit, standing the same way. Their face does not show.'
            : someone + (front ? '' : ' Seen from straight behind, so the face does not show.');
    const outfit = upload
        ? 'Everything else the person wears stays as in Image 1, neat and smooth, without messy creases.'
        : turned
            ? 'The rest of the outfit is exactly as in Image 1.'
            : 'The rest of the outfit is plain and quiet: with a top, plain, smooth, pressed trousers or jeans in grey, black or navy and clean white sneakers; with trousers or shorts, a plain, smooth white or grey T-shirt and clean white sneakers. Nothing is worn under or over the garment that the packshot does not show.';

    // As photo needs a photo: an AI model gets white
    const bg = o.background === 'photo' && !upload && !turned ? 'white' : o.background;
    const goneRoom = upload ? ' The room, wall and floor of the model photo are gone.' : '';
    const background = bg === 'studio'
        ? (turned ? 'Background: exactly the studio backdrop of Image 1: the same light grey, the same floor and light, and one soft shadow under the shoes.'
                  : 'Background: a seamless light-grey studio backdrop (about #ECECEE) that curves into a matching floor, softly and evenly lit, with one soft shadow under the shoes.' + goneRoom + ' No wall texture, corner, door, window, props or horizon line.')
        : bg === 'photo'
            ? (turned ? 'Background: exactly the background of Image 1, as if the camera had walked around the person in the same place.'
                      : 'Background: the background of the model photo (Image 1), exactly as it is.')
            : 'Background: a high-key white studio: the background is blown out to pure white, #FFFFFF, seamless all round, with no visible floor, corner or horizon and no shadow on it.' + goneRoom;
    const check = bg === 'studio' ? 'The backdrop is seamless light grey with one soft shadow under the shoes, and nothing else.'
        : bg === 'photo' ? (turned ? 'The background matches Image 1.' : 'The background is the model photo\'s own, unchanged.')
        : 'The background is pure white #FFFFFF from edge to edge, and the photo fills the whole frame: no border, panel or bars.';

    const fields: Record<string, string> = {
        VIEW: o.view,
        IMAGES: lines.join('\n'),
        PERSON: person,
        OUTFIT: outfit,
        FORMAT: PACKSHOT_FORMATS[o.aspect || '1:1'] || PACKSHOT_FORMATS['1:1'],
        BACKGROUND: background,
        BACKGROUND_CHECK: check,
    };
    return fields;
}
// What the model images are made from, from the job (bulk builds its own) or the page.
const modelChoices = (ctx: any) => ({ background: modelBackgroundChoice(ctx), aspect: ctx?.outputAspect || (state as any)?.billede?.outputAspect || '1:1' });
// The detail close-up only: the neck label is inside the collar and does not show on a person.
const wornCloseups = (ctx: any) => detailRefImgs(ctx).filter(f => (f as any).__closeup !== 'necktag');

// ── The product texts ──
// The brand, style number and colour code typed in Packshot, for the texts.
function productLine(): string {
    const val = (id: string) => (((document.getElementById(id) as HTMLInputElement | null)?.value) || '').trim();
    const parts = [
        String((window as any).__packshotBrandName || '') && 'Brand: ' + (window as any).__packshotBrandName + '.',
        val('styleNr') && 'Style number: ' + val('styleNr') + '.',
        val('colorCode') && 'Colour: ' + val('colorCode') + '.',
    ].filter(Boolean);
    return parts.length ? ' ' + parts.join(' ') : '';
}
function fillPromptFields(tpl: string, fields: Record<string, string>): string {
    return String(tpl || '').replace(/\{\{([A-Z_]+)\}\}/g, (all, k: string) => (k in fields ? fields[k] : all));
}

// 'white' (default) | 'studio' | 'photo' (keep whatever the supplied model photo shows).
// Falls back to the live UI setting, because bulk runs build their own ctx objects.
function modelBackgroundChoice(ctx: any): 'white' | 'studio' | 'photo' {
    const fromState = (typeof state !== 'undefined' && state.billede) ? (state.billede as any).modelBackground : '';
    const v = String(ctx?.modelBackground || fromState || 'white').toLowerCase();
    return (v === 'studio' || v === 'photo') ? v : 'white';
}

// True when the user picked the grey studio background (drives the verification + retry below).
function wantsStudioBackground(ctx: any): boolean {
    return modelBackgroundChoice(ctx) === 'studio';
}

// ── Background-match check (studio only) ─────────────────────
// Prompt obedience is not guaranteed: the back view is generated by a "rotate the camera"
// prompt whose own text demands pure white, and it has been observed inventing a completely
// different setting (a plastered wall and a dark floor) instead of copying the front's studio.
// So we VERIFY instead of hoping: sample the background patches of both images and compare.
// Cheap, deterministic, and it only ever costs an extra generation when it actually went wrong.
async function sampleBackgroundPatches(img: ImageResult): Promise<number[][] | null> {
    try {
        const el: HTMLImageElement = await new Promise((res, rej) => {
            const i = new Image();
            i.onload = () => res(i);
            i.onerror = rej;
            i.src = `data:${img.mime || 'image/png'};base64,${img.data}`;
        });
        const w = el.naturalWidth || el.width, h = el.naturalHeight || el.height;
        if (!w || !h) return null;
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const cx = canvas.getContext('2d');
        if (!cx) return null;
        cx.drawImage(el, 0, 0);
        // Four patches that the model itself never occupies: the two top corners (backdrop) and
        // the two lower outer corners (floor). The model stands in the middle, so these stay background.
        const boxes: [number, number][] = [
            [0.06, 0.06], [0.94, 0.06],
            [0.08, 0.92], [0.92, 0.92]
        ];
        const out: number[][] = [];
        for (const [fx, fy] of boxes) {
            const px = Math.min(w - 9, Math.max(0, Math.round(fx * w) - 4));
            const py = Math.min(h - 9, Math.max(0, Math.round(fy * h) - 4));
            const d = cx.getImageData(px, py, 8, 8).data;
            let r = 0, g = 0, b = 0, n = 0;
            for (let i = 0; i < d.length; i += 4) { r += d[i]; g += d[i + 1]; b += d[i + 2]; n++; }
            out.push([r / n, g / n, b / n]);
        }
        return out;
    } catch {
        return null;
    }
}

// Max per-channel difference between the two images' background patches. -1 = couldn't compare.
async function backgroundDifference(a: ImageResult, b: ImageResult): Promise<number> {
    const pa = await sampleBackgroundPatches(a);
    const pb = await sampleBackgroundPatches(b);
    if (!pa || !pb || pa.length !== pb.length) return -1;
    let worst = 0;
    for (let i = 0; i < pa.length; i++) {
        for (let c = 0; c < 3; c++) worst = Math.max(worst, Math.abs(pa[i][c] - pb[i][c]));
    }
    return worst;
}

const BG_MATCH_TOLERANCE = 26;   // per-channel; above this the two views are not the same set

// Does this image actually look like it was shot on a light-grey studio backdrop? Returns null when
// it does, otherwise a short reason. This is the check that catches the real failures: a room with a
// dark floor, a coloured wall, or a plain white knockout where a studio was asked for. Deliberately
// loose — a studio backdrop is light, neutral and fairly even, and that is all we test.
const STUDIO_MIN_LUM = 168;   // below this a patch is a dark floor / shadow / room, not grey paper
const STUDIO_MAX_LUM = 248;   // above this it is a white knockout, not a photographed backdrop
const STUDIO_MAX_SPREAD = 24; // above this the backdrop has a colour cast
function studioBackdropProblem(patches: number[][] | null): string | null {
    if (!patches || !patches.length) return null;   // couldn't measure → never block
    const lum = (p: number[]) => (p[0] * 299 + p[1] * 587 + p[2] * 114) / 1000;
    const spread = (p: number[]) => Math.max(p[0], p[1], p[2]) - Math.min(p[0], p[1], p[2]);
    const dark = patches.filter(p => lum(p) < STUDIO_MIN_LUM).length;
    if (dark) return `${dark} of ${patches.length} background corners are dark (a floor, a shadow or a room — not a grey studio backdrop)`;
    const tinted = patches.filter(p => spread(p) > STUDIO_MAX_SPREAD).length;
    if (tinted) return `${tinted} of ${patches.length} background corners have a colour cast`;
    if (patches.every(p => lum(p) > STUDIO_MAX_LUM)) return 'the background is plain white, not a grey studio backdrop';
    return null;
}

// ── Vision quality check (used by the opt-in stop-gate below) ──
const QA_PROMPT = `You are a strict product-image QA inspector for a fashion e-commerce pipeline. The FIRST image(s) are the REFERENCE garment (and optional close-ups); the LAST image is the generated OUTPUT. Compare them and score the OUTPUT 0-100 on each criterion, being harsh on small details. Return ONLY valid JSON, no prose:
{"product_fidelity_score":0,"detail_quality_score":0,"color_preservation_score":0,"background_score":0,"issues":[],"failure_reasons":[],"recommended_action":"approve"}
Rules: detail_quality_score covers neck tag, labels, logos, text, prints, stitching. If detail_quality_score < 70 OR any garment text is hallucinated/garbled OR a logo/label is invented or missing, recommended_action must be "regenerate" or "reject". If product_fidelity_score < 60, recommended_action must be "reject".
IGNORE a small "AI-generet" wordmark/badge in a corner of the OUTPUT image: it is a disclosure label added deliberately after generation, it is not part of the garment and must never count as an invented logo, print or text.
The OUTPUT may deliberately be shot on a different background than the REFERENCE (e.g. a light-grey studio backdrop with a soft floor shadow instead of white) — that is a chosen output setting, so score background_score on how clean and even the backdrop is, and NEVER lower the other scores or recommend regenerate/reject just because the background differs from the reference.
WHEN THE OUTPUT SHOWS A PERSON WEARING THE GARMENT, check the neck: the collar opening must be filled by the model's own neck and throat. If fabric, the inside of the collar, an inner neck tape or an inner brand/size label is drawn where the neck should be — so the head looks stuck on behind the garment — set product_fidelity_score below 60 and recommended_action to "regenerate", and say so in failure_reasons.`;

interface QAResult {
    product_fidelity_score?: number;
    detail_quality_score?: number;
    color_preservation_score?: number;
    background_score?: number;
    issues?: string[];
    failure_reasons?: string[];
    recommended_action?: string;
}

// Best-effort vision QA. Fail-OPEN: any error returns null so the pipeline never breaks
// because of the check. Uses geminiText (a vision-capable text model).
async function evaluateGarment(refImgs: File[], out: ImageResult): Promise<QAResult | null> {
    try {
        if (typeof geminiText !== 'function') return null;
        const outFile = await base64ToFile(out.data, out.mime, 'qa-output.png');
        const raw = await geminiText(QA_PROMPT, [...refImgs, outFile]);
        const clean = raw.replace(/```json/gi, '').replace(/```/g, '').trim();
        const start = clean.indexOf('{');
        const end = clean.lastIndexOf('}');
        if (start === -1 || end === -1) return null;
        return JSON.parse(clean.slice(start, end + 1)) as QAResult;
    } catch {
        return null;
    }
}

// Stop-gate decision (doc §G.4): a QA result blocks the flow when the model says so,
// or when a detail/fidelity score falls below the safety thresholds. Null QA (the check
// failed or was skipped) never blocks — the gate is strictly fail-open.
const QA_DETAIL_MIN = 70;
const QA_FIDELITY_MIN = 60;
function qaBlocks(qa: QAResult | null): boolean {
    if (!qa) return false;
    const act = String(qa.recommended_action || '').toLowerCase();
    if (act === 'reject' || act === 'regenerate') return true;
    if (typeof qa.detail_quality_score === 'number' && qa.detail_quality_score < QA_DETAIL_MIN) return true;
    if (typeof qa.product_fidelity_score === 'number' && qa.product_fidelity_score < QA_FIDELITY_MIN) return true;
    return false;
}
function qaSummary(slot: string, qa: QAResult): string {
    const r = (n?: number) => (typeof n === 'number' ? String(n) : '?');
    return `QA ${slot} — fidelity ${r(qa.product_fidelity_score)}, detail ${r(qa.detail_quality_score)}, colour ${r(qa.color_preservation_score)} → ${qa.recommended_action || 'n/a'}`;
}

// Persist a per-output generation metadata record server-side (app_state) so quality can be
// measured, compared across prompt versions and debugged later (doc §G.1). Best-effort /
// fail-open: a logging failure must never break or block image generation.
async function postGenerationMeta(record: Record<string, any>): Promise<void> {
    try {
        if (typeof apiFetch !== 'function' || !record || !record.generation_id) return;
        await apiFetch('/api/generations/' + encodeURIComponent(record.generation_id), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(record)
        });
    } catch { /* fail-open */ }
}

// Best-of-N (opt-in, doc §B/§C P2): generate `n` candidates, score each with vision-QA and
// return the highest-scoring one. n<=1 (the default) just runs `gen()` once — IDENTICAL to the
// previous behaviour, no extra credits. Needs QA on to have a selection signal; with QA off it
// falls back to the first candidate. The chosen candidate's QA is reported via onPicked so the
// caller can store it (and avoid re-scoring).
async function generateBestOfN(
    n: number,
    qaRefs: File[],
    gen: () => Promise<ImageResult>,
    label: string,
    log?: (m: string) => void,
    onPicked?: (qa: QAResult | null) => void
): Promise<ImageResult> {
    if (!(n > 1)) return await gen();
    let best: ImageResult | null = null;
    let bestScore = -1;
    let bestQa: QAResult | null = null;
    for (let i = 0; i < n; i++) {
        if (log) log(`  [best-of-${n}] ${label} candidate ${i + 1}/${n}…`);
        let cand: ImageResult;
        try { cand = await gen(); } catch { if (log) log(`  ⚠ [best-of-${n}] candidate ${i + 1} failed`); continue; }
        const qa = await evaluateGarment(qaRefs, cand);
        const score = qa ? ((qa.detail_quality_score ?? 0) + (qa.product_fidelity_score ?? 0)) : 0;
        if (log && qa) log(`  [best-of-${n}] candidate ${i + 1}: detail ${qa.detail_quality_score ?? '?'}, fidelity ${qa.product_fidelity_score ?? '?'}`);
        if (!best || score > bestScore) { best = cand; bestScore = score; bestQa = qa; }
    }
    if (!best) return await gen(); // every candidate threw → one plain attempt so the phase still produces something
    if (log) log(`  [best-of-${n}] ${label}: picked best (combined score ${bestScore})`);
    if (onPicked) onPicked(bestQa);
    return best;
}

// The floor photo kept in the packshot (2026-10-20): the image model at times keeps the bottom
// of the floor photo, its floor blurred into the white. The quality check judges the garment
// and passed one such back at 9.7, so it is measured here instead: a packshot is white all
// along its edges (the garment keeps a margin). Clean ones measure 253-255 there; every one
// with the floor was below 225 along the bottom. Such a picture is made once more, told why.
async function packshotEdgeProblem(img: ImageResult): Promise<{ bad: number; where: string } | null> {
    try {
        const el: HTMLImageElement = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = `data:${img.mime || 'image/png'};base64,${img.data}`; });
        const w = el.naturalWidth, h = el.naturalHeight;
        if (!w || !h) return null;
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const cx = c.getContext('2d');
        if (!cx) return null;
        cx.drawImage(el, 0, 0);
        const s = Math.max(4, Math.round(Math.min(w, h) * 0.02));
        const notWhite = (fx: number, fy: number) => {
            const px = Math.min(w - s, Math.max(0, Math.round(fx * w - s / 2))), py = Math.min(h - s, Math.max(0, Math.round(fy * h - s / 2)));
            const d = cx.getImageData(px, py, s, s).data;
            let r = 0, g = 0, b = 0, n = 0;
            for (let i = 0; i < d.length; i += 4) { r += d[i]; g += d[i + 1]; b += d[i + 2]; n++; }
            r /= n; g /= n; b /= n;
            return (r * 299 + g * 587 + b * 114) / 1000 < 225 || Math.max(r, g, b) - Math.min(r, g, b) > 20;
        };
        const sides: Record<string, number> = { bottom: 0, top: 0, left: 0, right: 0 };
        for (let i = 0; i <= 8; i++) {
            const f = 0.03 + i * (0.94 / 8);
            if (notWhite(f, 0.97)) sides.bottom++;
            if (notWhite(f, 0.03)) sides.top++;
            if (notWhite(0.03, f)) sides.left++;
            if (notWhite(0.97, f)) sides.right++;
        }
        const bad = sides.bottom + sides.top + sides.left + sides.right;
        // a sleeve near a side is not a floor: the bottom, or several places
        if (!sides.bottom && bad < 4) return null;
        return { bad, where: Object.keys(sides).filter(k => sides[k]).join(', ') };
    } catch { return null; }
}
const PACKSHOT_EDGE_RETRY = (where: string) => '\n\nThe last attempt kept part of the floor photo in the picture: along the ' + where + ' of the frame it was not white. Every pixel outside the garment is pure white #FFFFFF, from edge to edge: nothing of the floor photo, its floor or its light, is in the picture.';
async function withWhiteEdges(make: (note: string) => Promise<ImageResult>, onLog?: (msg: string) => void): Promise<ImageResult> {
    const first = await make('');
    const p1 = await packshotEdgeProblem(first);
    if (!p1) return first;
    onLog?.('  ⚠ The edge of the picture is not white along the ' + p1.where + ' (the floor photo kept): made once more');
    const second = await make(PACKSHOT_EDGE_RETRY(p1.where));
    const p2 = await packshotEdgeProblem(second);
    if (!p2) { onLog?.('  ✓ White edges'); return second; }
    onLog?.('  ⚠ Still not white along the ' + p2.where + ': the better of the two is kept');
    return p2.bad < p1.bad ? second : first;
}

// The size and format of a picture made for a library entry: made again later, in the
// person's library, it keeps the entry's own settings, not what the Packshot tab is set to now.
interface PictureShape { aspect?: string; resolution?: string; background?: string; }
const shapeOpts = (shape?: PictureShape) => shape ? { aspect: shape.aspect, resolution: shape.resolution, fit: false } : {};
// contain on white at the shape's size (as fitImageToOutputAspect does with the tab's), as a PNG
async function fitToShape(res: ImageResult, shape: PictureShape): Promise<ImageResult> {
    const ratios: Record<string, number[]> = typeof OUTPUT_ASPECT_RATIOS !== 'undefined' ? OUTPUT_ASPECT_RATIOS : {};
    const sides: Record<string, number> = typeof OUTPUT_RESOLUTION_LONG_SIDE !== 'undefined' ? OUTPUT_RESOLUTION_LONG_SIDE : {};
    const [rw, rh] = ratios[shape.aspect || '1:1'] || [1, 1];
    const long = sides[shape.resolution || '2k'] || 2048;
    const W = rw >= rh ? long : Math.round(long * rw / rh), H = rw >= rh ? Math.round(long * rh / rw) : long;
    const el: HTMLImageElement = await new Promise((ok, no) => { const i = new Image(); i.onload = () => ok(i); i.onerror = no; i.src = `data:${res.mime || 'image/png'};base64,${res.data}`; });
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const cx = c.getContext('2d')!;
    cx.fillStyle = '#FFFFFF';
    cx.fillRect(0, 0, W, H);
    const k = Math.min(W / el.naturalWidth, H / el.naturalHeight);
    cx.imageSmoothingQuality = 'high';
    cx.drawImage(el, (W - el.naturalWidth * k) / 2, (H - el.naturalHeight * k) / 2, el.naturalWidth * k, el.naturalHeight * k);
    return { ...res, data: c.toDataURL('image/png').split(',')[1], mime: 'image/png' };
}

// How a picture was made, kept with it for the automatic fix: its prompt (and the template's
// fields), the images sent, which of them is the garment's photo, the kind of garment.
interface MadeWith { promptKey?: string; fields?: Record<string, string>; prompt?: string; kind?: string; inputs?: File[]; refIndex?: number; aspect?: string; resolution?: string; }
const madeWith = (res: ImageResult, m: MadeWith): ImageResult => Object.assign(res, { made: m });

// ── A picture that fails its check, made again at once (2026-10-21) ──
// Simon: "Den skal jo også selv ændre billedet ... der skal ikke være nogen grund til at jeg skal
// gøre det manuelt". The quality check is often wrong on small things (a real flag on a tab, the
// texture), so a picture it fails is looked at closely first (Gemini 3.1 Pro, on the server, from
// the month's budget). Only when the close look fails it too, it is made again with the close
// look's fixes, looked at again, and the better of the two is kept. Once: a picture that fails
// twice is a case for the prompt doctor (src/autofix.ts).
const LOOK_RANK: Record<string, number> = { fail: 0, minor: 1, pass: 2 };
const asDataUrl = (r: ImageResult) => 'data:' + (r.mime || 'image/png') + ';base64,' + r.data;
async function fileDataUrl(f: File, size = 1600): Promise<string> {
    const bmp = await createImageBitmap(f);
    const k = Math.min(1, size / Math.max(bmp.width, bmp.height));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(bmp.width * k)); c.height = Math.max(1, Math.round(bmp.height * k));
    const x = c.getContext('2d')!;
    x.fillStyle = '#FFFFFF'; x.fillRect(0, 0, c.width, c.height);
    x.drawImage(bmp, 0, 0, c.width, c.height);
    if ((bmp as any).close) (bmp as any).close();
    return c.toDataURL('image/jpeg', 0.88);
}
async function closeLook(res: ImageResult, ref: File | null | undefined, slot: string): Promise<{ id: string; review: any } | null> {
    try {
        const r = await apiFetch('/api/assess', { method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ image: asDataUrl(res), ref: ref ? await fileDataUrl(ref, 1024) : undefined, tool: 'packshot', slot, prompt: (res as any).made?.prompt }) });
        return r.ok ? await r.json() : null;
    } catch { return null; }
}
async function remakeAllowed(resolution?: string): Promise<boolean> {
    try {
        const r = await apiFetch('/api/autofix/allow', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ resolution: resolution || '2k' }) });
        return r.ok && !!(await r.json()).ok;
    } catch { return false; }
}
async function selfFix(ctx: any, slot: string, res: ImageResult, refs: File[], remake: (instruction: string) => Promise<ImageResult>, log: (m: string) => void): Promise<ImageResult> {
    if (!ctx || !ctx.autoFix || ctx.qaGate === false || !res || !res.data || typeof evaluateGarment !== 'function') return res;
    const qa = await evaluateGarment(refs, res);
    ctx.qaPre = ctx.qaPre || {};
    ctx.qaPre[slot] = qa;
    (res as any).qa = qa;
    if (!qaBlocks(qa)) return res;
    const look = await closeLook(res, refs[0], slot);
    if (!look) return res;
    (res as any).look = look;
    if (look.review.verdict !== 'fail') {
        (res as any).selfFix = 'kept';
        log('  ✓ ' + slot + ': the quality check found faults, a close look did not (' + look.review.verdict + ', ' + look.review.score + '/10): kept');
        return res;
    }
    const critical = (look.review.defects || []).filter((d: any) => d.severity === 'critical').map((d: any) => d.title).join('; ');
    if (!(await remakeAllowed(ctx.outputResolution))) { log('  ⚠ ' + slot + ' failed a close look (' + critical + '), but the automatic fix has used this month\'s budget'); return res; }
    log('  ⚠ ' + slot + ' failed a close look (' + critical + '): made again at once, with its fixes');
    let res2: ImageResult;
    try { res2 = await remake(look.review.fix_prompt || buildQaFixInstruction(qa)); }
    catch (e) { (res as any).selfFix = 'tried'; log('  ⚠ ' + slot + ' could not be made again: ' + ((e as Error).message || 'error')); return res; }
    const qa2 = await evaluateGarment(refs, res2);
    const look2 = await closeLook(res2, refs[0], slot);
    const better = !!look2 && (LOOK_RANK[look2.review.verdict] > LOOK_RANK[look.review.verdict] || (look2.review.verdict === look.review.verdict && look2.review.score > look.review.score));
    const kept: any = better ? res2 : res;
    kept.qa = better ? qa2 : qa;
    kept.look = better ? look2 : look;
    kept.firstLook = look.review;
    kept.selfFix = kept.look.review.verdict === 'fail' ? 'tried' : 'fixed';
    ctx.qaPre[slot] = kept.qa;
    log(kept.selfFix === 'fixed' ? '  ✓ ' + slot + ' made again: ' + kept.look.review.verdict + ', ' + kept.look.review.score + '/10' : '  ⚠ ' + slot + ' still fails after being made again: the prompt doctor takes it');
    return kept;
}

async function buildFrontPackshot(front: File, isFlat: boolean, preferOpenAi: boolean = false, instruction: string = '', onLog?: (msg: string) => void, detailImgs: File[] = [], shape?: PictureShape): Promise<ImageResult> {
    const kind = await garmentKind(front, onLog);
    const choices = shape ? { aspect: shape.aspect || '1:1', background: shape.background || 'white' } : packshotChoices();
    const fields = packshotPromptFields({ view: 'front', flat: isFlat, withFront: false, closeups: detailImgs, kind, ...choices });
    const prompt = appendUserInstruction(buildPackshotPrompt({ view: 'front', flat: isFlat, withFront: false, closeups: detailImgs, kind, ...choices }), instruction);
    const inputs = [front, ...detailImgs];
    const res = await withWhiteEdges(note => geminiImage(prompt + note, inputs, { preferOpenAi, onLog, promptAsIs: true, ...shapeOpts(shape) }), onLog);
    return madeWith(shape ? await fitToShape(res, shape) : res, { promptKey: 'PACKSHOT', fields, prompt, kind, inputs, refIndex: 0, aspect: choices.aspect, resolution: shape?.resolution });
}

// Builds the back packshot in ONE AI pass, then snaps the background to pure
// white deterministically (no extra AI pass, no color drift). An optional
// free-text user instruction from the regenerate popup is appended to the prompt.
async function buildBackPackshot(back: File, isFlat: boolean, preferOpenAi: boolean = false, instruction: string = '', frontPackshot?: ImageResult | null, onLog?: (msg: string) => void, detailImgs: File[] = [], shape?: PictureShape): Promise<ImageResult> {
    // 1-step. The finished front is passed as the colour authority (Image 1), for the
    // invisible mannequin and the flat-lay alike: the back's floor photo has its own
    // light (on 2026-10-07 a flat back without it came out bluish). The neck label is
    // inside the collar and does not show from the back: sent to the back, it was put
    // on the outside of it, so the back gets the detail close-up only.
    const closeups = detailImgs.filter(f => (f as any).__closeup !== 'necktag');
    const kind = await garmentKind(back);
    const choices = shape ? { aspect: shape.aspect || '1:1', background: shape.background || 'white' } : packshotChoices();
    const o: PackshotPromptOpts = { view: 'back', flat: isFlat, withFront: !!frontPackshot, closeups, kind, ...choices };
    const prompt = appendUserInstruction(buildPackshotPrompt(o), instruction);
    const inputs = frontPackshot ? [await base64ToFile(frontPackshot.data, frontPackshot.mime, 'front-ps-ref'), back, ...closeups] : [back, ...closeups];
    const res = await withWhiteEdges(note => geminiImage(prompt + note, inputs, { preferOpenAi, onLog, promptAsIs: true, ...shapeOpts(shape) }), onLog);
    return madeWith(shape ? await fitToShape(res, shape) : res, { promptKey: 'PACKSHOT', fields: packshotPromptFields(o), prompt, kind, inputs, refIndex: frontPackshot ? 1 : 0, aspect: choices.aspect, resolution: shape?.resolution });
}

async function phasePackshots(ctx: any = state.billede): Promise<void> {
    const front = ctx.uploads.front;
    const back = ctx.uploads.back;
    const log = (msg: string) => {
        if (ctx === state.billede) appendLog(msg);
        else if (ctx && ctx.onLog) ctx.onLog(msg);
    };

    if (!front || !back) {
        log('  ⚠ Skipping Phase 1 — missing front/back upload');
        if (ctx === state.billede) updatePhaseStatus(1, 'completed');
        return;
    }
    sameGarment(back, front);
    const detailImgs = await closeupsFor(ctx, log);
    // Output style — chosen by the user on the Packshot page before generating.
    // 'packshot' = invisible-mannequin / ghost-mannequin 3D form (default).
    // 'flat'     = flat-lay product photo, garment laid flat, shot top-down.
    const isFlat = ctx.outputStyle === 'flat';
    const preferOpenAi = ctx.outputModel === 'openai';

    log(isFlat
        ? '\n── PHASE 1: Flat-Lay Product Photos ──'
        : '\n── PHASE 1: Invisible Mannequin Packshots ──');
    if (ctx === state.billede) updatePhaseStatus(1, 'active');

    // Best-of-N (opt-in): generate N candidates per side and keep the highest QA-scoring one.
    // bestN === 1 (default) runs exactly one generation per side — unchanged behaviour, no extra cost.
    const bestN = Math.max(1, Math.min(4, Number(ctx.bestOfN ?? (state as any)?.billede?.bestOfN) || 1));
    if (detailImgs.length) log(`  Using ${detailImgs.length} close-up reference(s) for detail fidelity`);
    if (bestN > 1) log(`  Best-of-${bestN}: generating ${bestN} candidates per side, auto-picking the best by QA score`);

    log(`  Generating front ${isFlat ? 'flat-lay' : 'packshot'}...`);
    let packFront = await generateBestOfN(
        bestN, [front, ...detailImgs],
        () => buildFrontPackshot(front, isFlat, preferOpenAi, '', log, detailImgs),
        'front packshot', log
    );
    packFront = await selfFix(ctx, 'packshot-front', packFront, [front, ...detailImgs], ins => buildFrontPackshot(front, isFlat, preferOpenAi, ins, log, detailImgs), log);
    ctx.results.packshotFront = packFront;
    if (ctx === state.billede) displayOutputImage('packshot-front', packFront.data);
    log(`  ✓ Front ${isFlat ? 'flat-lay' : 'packshot'}`);

    log(`  Generating back ${isFlat ? 'flat-lay' : 'packshot'}...`);
    let packBack = await generateBestOfN(
        bestN, [back, ...detailImgs],
        () => buildBackPackshot(back, isFlat, preferOpenAi, '', ctx.results.packshotFront, log, detailImgs),
        'back packshot', log
    );
    packBack = await selfFix(ctx, 'packshot-back', packBack, [back, ...detailImgs], ins => buildBackPackshot(back, isFlat, preferOpenAi, ins, ctx.results.packshotFront, log, detailImgs), log);
    ctx.results.packshotBack = packBack;
    if (ctx === state.billede) displayOutputImage('packshot-back', packBack.data);
    log(`  ✓ Back ${isFlat ? 'flat-lay' : 'packshot'}`);

    if (ctx === state.billede) updatePhaseStatus(1, 'completed');
}

async function phaseDescription(ctx: any = state.billede): Promise<void> {
    const log = (msg: string) => {
        if (ctx === state.billede) appendLog(msg);
        else if (ctx && ctx.onLog) ctx.onLog(msg);
    };

    const front = ctx.uploads.front;
    if (!front) {
        log('  ⚠ Skipping Phase 2 — missing front upload');
        if (ctx === state.billede) updatePhaseStatus(2, 'completed');
        return;
    }
    log('\n── PHASE 2: Product Description ──');
    if (ctx === state.billede) updatePhaseStatus(2, 'active');

    const tag: any = ctx.uploads.necktag && await brandLabelUsed(ctx, log) ? ctx.uploads.necktag : null;
    const descImgs: File[] = [front,
        ...(ctx.uploads.detail  ? [ctx.uploads.detail]  : []),
        ...(tag ? [tag] : [])];
    const imageLines = ['Image 1: the front of the garment, photographed lying on a floor.'];
    if (ctx.uploads.detail) imageLines.push('Image ' + (imageLines.length + 1) + ': a close-up of a detail of the garment.');
    if (tag) imageLines.push('Image ' + (imageLines.length + 1) + ': a close-up of the garment\'s neck label' + (tag.__brandName ? ' (' + tag.__brandName + '\'s label)' : '') + '.');
    log(`  Analyzing ${descImgs.length} image(s)...`);
    const description = await geminiText(fillPromptFields(PROMPTS.DESCRIPTION, { PRODUCT: productLine(), IMAGES: imageLines.join('\n') }), descImgs);
    ctx.results.description = description;
    log('  ✓ Product description generated');

    if (ctx === state.billede) updatePhaseStatus(2, 'completed');
}

async function phaseTextSeo(ctx: any = state.billede): Promise<void> {
    const log = (msg: string) => {
        if (ctx === state.billede) appendLog(msg);
        else if (ctx && ctx.onLog) ctx.onLog(msg);
    };

    if (!ctx.results.description) {
        log('  ⚠ Skipping Phase 3 — requires Description (Phase 2)');
        if (ctx === state.billede) updatePhaseStatus(3, 'completed');
        return;
    }
    const description = ctx.results.description;
    log('\n── PHASE 3: Shopify Text + SEO ──');
    if (ctx === state.billede) updatePhaseStatus(3, 'active');

    log('  Generating Shopify text...');
    const textFields = { PRODUCT: productLine(), DESC: description };
    const shopify = await geminiText(fillPromptFields(PROMPTS.SHOPIFY, textFields));
    ctx.results.shopify = shopify;
    if (ctx === state.billede) document.getElementById('shopifyText')!.textContent = shopify;
    log('  ✓ Shopify text generated');

    log('  Generating SEO Meta...');
    const seo = await geminiText(fillPromptFields(PROMPTS.SEO, textFields));
    ctx.results.seo = seo;
    if (ctx === state.billede) document.getElementById('seoText')!.textContent = seo.trim();
    log(`  ✓ SEO: ${seo.trim().substring(0, 80)}...`);

    log('  Generating JSON data...');
    const jsonRaw = await geminiText(fillPromptFields(PROMPTS.JSON_DATA, textFields));
    ctx.results.json = jsonRaw;
    try {
        const clean = jsonRaw.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
        if (ctx === state.billede) document.getElementById('jsonText')!.textContent = JSON.stringify(JSON.parse(clean), null, 2);
    } catch {
        if (ctx === state.billede) document.getElementById('jsonText')!.textContent = jsonRaw;
    }
    log('  ✓ JSON product data');

    if (ctx === state.billede) document.getElementById('textResults')!.classList.add('visible');
    if (ctx === state.billede) updatePhaseStatus(3, 'completed');
}

async function phaseModels(ctx: any = state.billede): Promise<void> {
    const log = (msg: string) => {
        if (ctx === state.billede) appendLog(msg);
        else if (ctx && ctx.onLog) ctx.onLog(msg);
    };

    // The detail close-up (the neck label does not show on a person).
    const detailImgs = wornCloseups(ctx);

    // Use packshots if available, otherwise fall back to raw uploads
    const packFront = ctx.results.packshotFront;
    const packBack = ctx.results.packshotBack;
    const rawFront = ctx.uploads.front;
    const rawBack = ctx.uploads.back;

    const hasPacks = !!packFront && !!packBack;
    const hasRaw = !!rawFront || !!rawBack;

    if (!hasPacks && !hasRaw) {
        log('\n  ⚠ Skipping Phase 4 — no images available (upload front/back or run Packshots first)');
        if (ctx === state.billede) updatePhaseStatus(4, 'completed');
        return;
    }

    const hasModel = !!ctx.uploads.model;
    const source = hasPacks ? 'packshots' : 'raw uploads';
    log(`\n── PHASE 4: Model Images ${hasModel ? '(with uploaded model)' : '(AI-generated model)'} · using ${source} · background: ${modelBackgroundChoice(ctx)} ──`);
    if (ctx === state.billede) updatePhaseStatus(4, 'active');

    // Build front/back reference files
    const frontRef = hasPacks
        ? await base64ToFile(packFront!.data, packFront!.mime, 'front-ps')
        : rawFront!;
    const backRef = hasPacks
        ? await base64ToFile(packBack!.data, packBack!.mime, 'back-ps')
        : rawBack || rawFront!;

    // One pass per side, from MODEL. (Until 2026-10-07 a second "refine" pass made each
    // image again from the packshots; the one prompt now says it all, at half the cost.)
    const choices = modelChoices(ctx);
    const preferOpenAi = ctx.outputModel === 'openai';
    log('  Generating front model image...');
    const frontOpts: ModelPromptOpts = { view: 'front', person: hasModel ? 'upload' : 'generate', withFront: false, fromPackshot: hasPacks, closeups: detailImgs, ...choices };
    const frontPrompt = buildModelPrompt(frontOpts);
    const frontInputs = hasModel ? [ctx.uploads.model, frontRef, ...detailImgs] : [frontRef, ...detailImgs];
    const frontMade = (prompt: string): MadeWith => ({ promptKey: 'MODEL', fields: modelPromptFields(frontOpts), prompt, inputs: frontInputs, refIndex: hasModel ? 1 : 0, aspect: choices.aspect, resolution: ctx.outputResolution });
    let modelFront = madeWith(await geminiImage(frontPrompt, frontInputs, { preferOpenAi, onLog: log, promptAsIs: true }), frontMade(frontPrompt));

    // Verify the front actually landed on a grey studio backdrop, and retry ONCE if it didn't.
    // Prompt obedience is not a given — especially when the model photo brings its own background —
    // so the result is measured instead of assumed. Costs an extra generation only when it went wrong.
    if (wantsStudioBackground(ctx)) {
        try {
            const problem = studioBackdropProblem(await sampleBackgroundPatches(modelFront));
            if (problem) {
                log(`    ⚠ Front view is not on a studio backdrop — ${problem}. Retrying once…`);
                const retry = await geminiImage(frontPrompt + MODEL_BG_RETRY, frontInputs, { preferOpenAi, onLog: log, promptAsIs: true });
                if (!studioBackdropProblem(await sampleBackgroundPatches(retry))) {
                    modelFront = madeWith(retry, frontMade(frontPrompt));
                    log('    ✓ Retry landed on the studio backdrop');
                } else {
                    log('    ⚠ Retry still is not a studio backdrop — keeping the first attempt. Try ↻ with your own instruction.');
                }
            } else {
                log('    ✓ Front view is on a grey studio backdrop');
            }
        } catch (e) { /* verification is best-effort — never block the phase */ }
    }
    modelFront = await selfFix(ctx, 'model-front', modelFront, [frontRef, ...detailImgs], async ins => madeWith(await geminiImage(appendUserInstruction(frontPrompt, ins), frontInputs, { preferOpenAi, onLog: log, promptAsIs: true }), frontMade(appendUserInstruction(frontPrompt, ins))), log);
    ctx.results.modelFront = modelFront;
    if (ctx === state.billede) displayOutputImage('model-front', modelFront.data);
    log('  ✓ Model front');

    log('  Generating back model image...');
    let modelBack;
    // The back is the front model image turned around (also when a model photo was uploaded:
    // one front-facing photo cannot show the person's back), so both are the same person,
    // outfit and setting.
    if (modelFront && modelFront.data) {
        const frontModelRef = await base64ToFile(modelFront.data, modelFront.mime, 'model-front-ref');
        const backPrompt = buildModelPrompt({ view: 'back', person: hasModel ? 'upload' : 'generate', withFront: true, fromPackshot: hasPacks, closeups: detailImgs, ...choices });
        modelBack = await geminiImage(backPrompt, [frontModelRef, backRef, ...detailImgs], { preferOpenAi, onLog: log, promptAsIs: true });

        // VERIFY the backdrop actually matches the front view, and retry ONCE if it doesn't.
        if (wantsStudioBackground(ctx)) {
            try {
                const problem = studioBackdropProblem(await sampleBackgroundPatches(modelBack));
                const diff = await backgroundDifference(modelFront, modelBack);
                if (problem || diff > BG_MATCH_TOLERANCE) {
                    log(`    ⚠ Back view background is off — ${problem || `it does not match the front (Δ${Math.round(diff)})`}. Retrying once…`);
                    const retry = await geminiImage(backPrompt + MODEL_BG_RETRY, [frontModelRef, backRef, ...detailImgs], { preferOpenAi, onLog: log, promptAsIs: true });
                    const retryProblem = studioBackdropProblem(await sampleBackgroundPatches(retry));
                    const retryDiff = await backgroundDifference(modelFront, retry);
                    if (!retryProblem && (retryDiff < 0 || retryDiff <= Math.max(diff, BG_MATCH_TOLERANCE))) {
                        modelBack = retry;
                        log(`    ✓ Retry matches the front studio (Δ${Math.round(retryDiff)})`);
                    } else {
                        log('    ⚠ Retry was no better — keeping the first back view. Use ↻ or ✨ AI-fix if the background is wrong.');
                    }
                } else {
                    log(`    ✓ Back view is on the same studio background as the front (Δ${Math.round(diff)})`);
                }
            } catch (e) { /* verification is best-effort — never block the phase */ }
        }
    } else {
        // Safety net: the front produced nothing to turn around, so the back is made from the packshot alone.
        const fallbackPrompt = buildModelPrompt({ view: 'back', person: 'generate', withFront: false, fromPackshot: hasPacks, closeups: detailImgs, ...choices });
        modelBack = await geminiImage(fallbackPrompt, [backRef, ...detailImgs], { preferOpenAi, onLog: log, promptAsIs: true });
    }
    if (modelBack && modelFront && modelFront.data) {
        const fm = await base64ToFile(modelFront.data, modelFront.mime, 'model-front-ref');
        const backOpts: ModelPromptOpts = { view: 'back', person: hasModel ? 'upload' : 'generate', withFront: true, fromPackshot: hasPacks, closeups: detailImgs, ...choices };
        const backPrompt = buildModelPrompt(backOpts);
        const backInputs = [fm, backRef, ...detailImgs];
        const backMade = (prompt: string): MadeWith => ({ promptKey: 'MODEL', fields: modelPromptFields(backOpts), prompt, inputs: backInputs, refIndex: 1, aspect: choices.aspect, resolution: ctx.outputResolution });
        if (!(modelBack as any).made) madeWith(modelBack, backMade(backPrompt));
        modelBack = await selfFix(ctx, 'model-back', modelBack, [backRef, ...detailImgs], async ins => madeWith(await geminiImage(appendUserInstruction(backPrompt, ins), backInputs, { preferOpenAi, onLog: log, promptAsIs: true }), backMade(appendUserInstruction(backPrompt, ins))), log);
    }
    ctx.results.modelBack = modelBack;
    if (ctx === state.billede) displayOutputImage('model-back', modelBack.data);
    log('  ✓ Model back');

    // Post-processing hook — currently a pass-through (cleanWhiteBackground returns the image
    // unchanged). It must stay background-agnostic: forcing white here would wipe the studio backdrop.
    if (ctx.results.modelFront) {
        ctx.results.modelFront = await cleanWhiteBackground(ctx.results.modelFront);
        if (ctx === state.billede) displayOutputImage('model-front', ctx.results.modelFront.data);
    }
    if (ctx.results.modelBack) {
        ctx.results.modelBack = await cleanWhiteBackground(ctx.results.modelBack);
        if (ctx === state.billede) displayOutputImage('model-back', ctx.results.modelBack.data);
    }

    if (ctx === state.billede) updatePhaseStatus(4, 'completed');
}

// ── Detail shots (phase 5) ───────────────────────────────────
// Close-ups made from the model shots, so the garment fills the picture and
// its detail is real: a full-body model shot shows the garment small, and no
// enlargement can add what is not there. Image 1 is the model shot (person,
// light, background), Image 2 the packshot (the truth for the garment), then
// the uploaded close-ups. Which ones are made is chosen in the Pipeline
// (state.billede.detailShots); each is its own output slot, detail-<id>.
interface DetailShot { id: string; slot: string; key: string; label: string; view: 'front' | 'back'; shows: string; }
const DETAIL_SHOTS: DetailShot[] = [
    { id: 'front',  slot: 'detail-front',  key: 'detailFront',  label: 'Front',  view: 'front', shows: 'the front of the garment around the chest, centred on its main front feature: the print, logo, pocket, buttons or placket' },
    { id: 'collar', slot: 'detail-collar', key: 'detailCollar', label: 'Collar', view: 'front', shows: 'the top of the garment: the collar, neckline, hood or waistband, and how it closes (buttons, zip or drawstring)' },
    { id: 'sleeve', slot: 'detail-sleeve', key: 'detailSleeve', label: 'Sleeve', view: 'front', shows: 'the end of one sleeve or trouser leg: the cuff or hem, with any badge, label or trim on it' },
    { id: 'fabric', slot: 'detail-fabric', key: 'detailFabric', label: 'Fabric', view: 'front', shows: 'a macro close-up of the fabric alone: a palm-sized patch on the chest, so close that the weave or knit fills the whole picture and no button, seam or edge is in it' },
    { id: 'back',   slot: 'detail-back',   key: 'detailBack',   label: 'Back',   view: 'back',  shows: 'the back of the garment between the shoulders, centred on its main back feature: a print, label, yoke or seam' },
];
const DEFAULT_DETAIL_SHOTS = ['front', 'collar', 'fabric'];

function detailShotsFor(ctx: any): DetailShot[] {
    const ids: string[] = Array.isArray(ctx?.detailShots) ? ctx.detailShots : DEFAULT_DETAIL_SHOTS;
    return DETAIL_SHOTS.filter(d => ids.includes(d.id));
}

function detailCloseupsNote(closeups: number): string {
    let out = '';
    for (let i = 0; i < closeups; i++) out += '\nImage ' + (3 + i) + ': a close-up of a detail of the garment, for that detail only.';
    return out;
}

// One detail shot, from the model shot and the packshot of its view.
async function buildDetailShot(d: DetailShot, ctx: any, instruction: string = '', onLog?: (msg: string) => void, shape?: PictureShape): Promise<ImageResult> {
    const r = ctx.results || {};
    const model: ImageResult | null = d.view === 'back' ? r.modelBack : r.modelFront;
    if (!model || !model.data) throw new Error(`The ${d.view} model image is needed first — run Model Images`);
    const pack: ImageResult | null = d.view === 'back' ? r.packshotBack : r.packshotFront;
    const garment: File | null = pack && pack.data
        ? await base64ToFile(pack.data, pack.mime, `${d.view}-ps`)
        : (d.view === 'back' ? (ctx.uploads?.back || null) : (ctx.uploads?.front || null));
    if (!garment) throw new Error(`The ${d.view} packshot or photo is needed first`);
    const modelFile = await base64ToFile(model.data, model.mime, `model-${d.view}`);
    const closeups = wornCloseups(ctx);
    const prompt = appendUserInstruction(String(PROMPTS.MODEL_DETAIL || '')
        .replace(/\{\{DETAIL\}\}/g, d.shows)
        .replace(/\{\{CLOSEUPS\}\}/g, detailCloseupsNote(closeups.length)), instruction);
    const res = await geminiImage(prompt, [modelFile, garment, ...closeups], { preferOpenAi: ctx.outputModel === 'openai', onLog, promptAsIs: true, ...shapeOpts(shape) });
    return shape ? await fitToShape(res, shape) : res;
}

async function phaseDetails(ctx: any = state.billede): Promise<void> {
    const log = (msg: string) => {
        if (ctx === state.billede) appendLog(msg);
        else if (ctx && ctx.onLog) ctx.onLog(msg);
    };
    const shots = detailShotsFor(ctx);
    if (!shots.length) {
        log('\n  ⚠ Skipping Phase 5 — no detail shots chosen');
        if (ctx === state.billede) updatePhaseStatus(5, 'completed');
        return;
    }
    log(`\n── PHASE 5: Detail Shots (${shots.map(d => d.label).join(', ')}) ──`);
    if (ctx === state.billede) updatePhaseStatus(5, 'active');
    for (const d of shots) {
        const model = d.view === 'back' ? ctx.results.modelBack : ctx.results.modelFront;
        if (!model || !model.data) { log(`  ⚠ ${d.label}: no ${d.view} model image — skipped`); continue; }
        log(`  Generating detail: ${d.label}...`);
        try {
            const res = await buildDetailShot(d, ctx, '', log);
            ctx.results[d.key] = res;
            if (ctx === state.billede) displayOutputImage(d.slot, res.data);
            log(`  ✓ Detail: ${d.label}`);
        } catch (e) {
            log(`  ⚠ Detail ${d.label} failed: ${(e as Error).message || 'error'}`);
        }
    }
    if (ctx === state.billede) updatePhaseStatus(5, 'completed');
}

// ── Model images in 4K ───────────────────────────────────────
// A full-body model image shows the garment small, so it is made 4K by
// FloorFlow's own upscaler on the server (src/upscale-4k.ts): free, a 2K image
// in 2-6 minutes on Railway (94 s idle, about 6 min busy), after any Studio
// look waiting (its jobs go first).
// Setting: Advanced → Model 4K (model4k, on unless set
// off). An image already 4K (Resolution 4K) is left as it is, and a failed
// upscale keeps the image as it was.
async function modelImageTo4K(res: ImageResult | null | undefined, label: string, log: (m: string) => void): Promise<ImageResult | null | undefined> {
    const up = (window as any).upscaleImageTo4K;
    if (!res || !res.data || typeof up !== 'function') return res;
    const src = `data:${res.mime || 'image/png'};base64,${res.data}`;
    const [w, h] = await new Promise<[number, number]>((resolve) => {
        const i = new Image();
        i.onload = () => resolve([i.naturalWidth, i.naturalHeight]);
        i.onerror = () => resolve([0, 0]);
        i.src = src;
    });
    const side = Math.max(w, h);
    if (!side || side > 2800) return res;
    log(`  4K: ${label}...`);
    let shown = -1;
    try {
        const r = await up(src, (done: number, total: number) => {
            const pct = total ? Math.floor(4 * done / total) * 25 : -1;
            if (pct >= 0 && pct !== shown) { shown = pct; log(`  4K ${label}: ${pct}%`); }
        }, { priority: 'low' });
        const data = await fileToBase64(new File([r.blob], `${label.replace(/\s+/g, '-')}-4k.jpg`, { type: 'image/jpeg' }));
        log(`  ✓ ${label} in 4K (${r.width}×${r.height})`);
        return Object.assign({}, res, { data, mime: 'image/jpeg' });
    } catch (e) {
        log(`  ⚠ 4K ${label} failed, kept at ${side} px: ${(e as Error).message || 'error'}`);
        return res;
    }
}
async function modelImagesTo4K(ctx: any, log: (m: string) => void): Promise<void> {
    if (!ctx || ctx.model4k === false || !ctx.results) return;
    for (const [key, slot, label] of [['modelFront', 'model-front', 'model front'], ['modelBack', 'model-back', 'model back']]) {
        if (!ctx.results[key]) continue;
        const before = ctx.results[key];
        ctx.results[key] = await modelImageTo4K(before, label, log);
        if (ctx === state.billede && ctx.results[key] !== before) displayOutputImage(slot, ctx.results[key].data);
    }
}

// ── Single-slot regeneration ─────────────────────────────────
// Re-generates ONE output image, used by the per-thumbnail "Regenerate"
// popup. The user picks a style (packshot vs flat-lay — packshot slots only)
// and an AI model (Gemini vs OpenAI/ChatGPT) for just that one slot.
interface RegenOpts {
    style?: 'packshot' | 'flat';
    model?: 'gemini' | 'openai';
    instruction?: string;
}

async function regenerateBilledeSlot(slot: string, opts: RegenOpts = {}): Promise<void> {
    const preferOpenAi = opts.model === 'openai';
    const isFlat = opts.style === 'flat';
    const u = state.billede.uploads;
    const r = state.billede.results;
    sameGarment(u.back, u.front);
    const detailImgs = slot === 'packshot-front' ? await closeupsFor(state.billede, appendLog) : detailRefImgs(state.billede);
    const modelLabel = preferOpenAi ? 'ChatGPT' : 'Gemini';

    appendLog(`\n── Regenerating ${slot} (${isFlat ? 'flat-lay' : 'packshot'} · ${modelLabel}) ──`);

    if (slot === 'packshot-front') {
        if (!u.front) throw new Error('Upload a front image first');
        const res = await buildFrontPackshot(u.front, isFlat, preferOpenAi, opts.instruction, undefined, detailImgs);
        r.packshotFront = res;
        displayOutputImage('packshot-front', res.data);

    } else if (slot === 'packshot-back') {
        if (!u.back) throw new Error('Upload a back image first');
        const res = await buildBackPackshot(u.back, isFlat, preferOpenAi, opts.instruction, r.packshotFront, undefined, detailImgs);
        r.packshotBack = res;
        displayOutputImage('packshot-back', res.data);

    } else if (slot === 'model-front') {
        const frontRef: File | null = r.packshotFront
            ? await base64ToFile(r.packshotFront.data, r.packshotFront.mime, 'front-ps')
            : u.front;
        if (!frontRef) throw new Error('Missing front image — upload one or run Packshots first');
        const worn = wornCloseups(state.billede);
        const prompt = appendUserInstruction(buildModelPrompt({ view: 'front', person: u.model ? 'upload' : 'generate', withFront: false, fromPackshot: !!r.packshotFront, closeups: worn, ...modelChoices(state.billede) }), opts.instruction);
        const rawFront = await geminiImage(prompt, u.model ? [u.model, frontRef, ...worn] : [frontRef, ...worn], { preferOpenAi, promptAsIs: true });
        const res = await cleanWhiteBackground(rawFront);
        r.modelFront = res;
        displayOutputImage('model-front', res.data);
        if ((state.billede as any).model4k !== false) {
            r.modelFront = (await modelImageTo4K(r.modelFront, 'model front', appendLog)) || res;
            displayOutputImage('model-front', r.modelFront!.data);
        }

    } else if (slot === 'model-back') {
        const backRef: File | null = r.packshotBack
            ? await base64ToFile(r.packshotBack.data, r.packshotBack.mime, 'back-ps')
            : (u.back || u.front);
        if (!backRef) throw new Error('Missing back image — upload one or run Packshots first');
        let res: ImageResult;
        const worn = wornCloseups(state.billede);
        // The front model image turned around, so the back is the same person in the same setting;
        // without one, a back made from the packshot alone.
        if (r.modelFront) {
            const fmRef = await base64ToFile(r.modelFront.data, r.modelFront.mime, 'model-front-ref');
            const prompt = appendUserInstruction(buildModelPrompt({ view: 'back', person: u.model ? 'upload' : 'generate', withFront: true, fromPackshot: !!r.packshotBack, closeups: worn, ...modelChoices(state.billede) }), opts.instruction);
            res = await geminiImage(prompt, [fmRef, backRef, ...worn], { preferOpenAi, promptAsIs: true });
        } else {
            const prompt = appendUserInstruction(buildModelPrompt({ view: 'back', person: 'generate', withFront: false, fromPackshot: !!r.packshotBack, closeups: worn, ...modelChoices(state.billede) }), opts.instruction);
            res = await geminiImage(prompt, [backRef, ...worn], { preferOpenAi, promptAsIs: true });
        }
        res = await cleanWhiteBackground(res);
        r.modelBack = res;
        displayOutputImage('model-back', res.data);
        if ((state.billede as any).model4k !== false) {
            r.modelBack = (await modelImageTo4K(r.modelBack, 'model back', appendLog)) || res;
            displayOutputImage('model-back', r.modelBack!.data);
        }

    } else if (DETAIL_SHOTS.some(d => d.slot === slot)) {
        const d = DETAIL_SHOTS.find(x => x.slot === slot)!;
        const res = await buildDetailShot(d, Object.assign({}, state.billede, { outputModel: preferOpenAi ? 'openai' : state.billede.outputModel }), opts.instruction);
        (r as any)[d.key] = res;
        displayOutputImage(slot, res.data);

    } else {
        throw new Error('Unknown slot: ' + slot);
    }

    appendLog(`  ✓ Regenerated ${slot}`);
}

// ── AI-fix — QA-guided repair of ONE output ──────────────────
// Same idea as regenerate, except the user doesn't have to describe what's wrong: the
// vision-QA inspector already saw it. We turn its concrete failure reasons into a fix
// instruction, re-run the normal single-slot generation with that instruction, then QA the
// result again and keep whichever version scored best. Nothing is thrown away silently —
// if the retry is worse, the original image is restored.
const AI_FIX_MAX_ROUNDS = 2;   // attempts per click before we stop and keep the best version

// Turns a QA verdict into a targeted, imperative fix instruction.
function buildQaFixInstruction(qa: QAResult | null): string {
    const problems: string[] = [];
    const push = (arr?: string[]) => (arr || []).forEach(x => {
        const t = String(x || '').trim();
        if (t && !problems.includes(t)) problems.push(t);
    });
    push(qa?.failure_reasons);
    push(qa?.issues);
    if (typeof qa?.detail_quality_score === 'number' && qa.detail_quality_score < QA_DETAIL_MIN) {
        problems.push('The small details (neck tag, labels, logos, text, prints, stitching) scored only ' + qa.detail_quality_score + '/100 — they are not faithful to the reference.');
    }
    if (typeof qa?.product_fidelity_score === 'number' && qa.product_fidelity_score < QA_FIDELITY_MIN) {
        problems.push('The garment itself scored only ' + qa.product_fidelity_score + '/100 — shape, cut or proportions drifted from the reference.');
    }
    if (typeof qa?.color_preservation_score === 'number' && qa.color_preservation_score < 80) {
        problems.push('The colour drifted from the reference (colour score ' + qa.color_preservation_score + '/100).');
    }
    if (!problems.length) problems.push('An automated quality check rejected the previous attempt without naming a specific reason — rebuild the output with maximum fidelity to the reference.');
    return 'FIX THE FOLLOWING FAULTS — an automated quality inspector compared the previous attempt with the reference image(s) and found these concrete problems:\n'
        + problems.map((p, i) => (i + 1) + '. ' + p).join('\n')
        + '\n\nProduce a corrected version that fixes every point above while keeping everything that was already correct. '
        + 'Do NOT restyle, recrop, recolour or "improve" anything else. If a label, tag, logo, print or text was invented or garbled, REMOVE the invented element and render that area as clean garment fabric rather than guessing new characters. '
        + 'If a real detail went missing, put it back exactly as the reference shows it — same position, size, angle and colour.';
}

// The reference images QA should compare a given slot against — the same pairing the
// pipeline's own stop-gate uses, so a fix is judged on the same basis it was flagged on.
async function qaRefsForSlot(slot: string, ctx: any = state.billede): Promise<File[]> {
    const u: any = ctx.uploads || {};
    const r: any = ctx.results || {};
    const closeups = [u.necktag && await brandLabelUsed(ctx) ? u.necktag : null, u.detail].filter((f: any): f is File => !!f);
    const asFile = async (res: any, name: string): Promise<File | null> =>
        (res && res.data) ? await base64ToFile(res.data, res.mime, name) : null;

    let primary: File | null = null;
    if (slot === 'packshot-front')      primary = u.front || null;
    else if (slot === 'packshot-back')  primary = u.back || u.front || null;
    else if (slot === 'model-front')    primary = (await asFile(r.packshotFront, 'qa-ref-front')) || u.front || null;
    else if (slot === 'model-back')     primary = (await asFile(r.packshotBack, 'qa-ref-back')) || u.back || u.front || null;
    else if (slot === 'detail-back')    primary = (await asFile(r.packshotBack, 'qa-ref-back')) || u.back || null;
    else if (slot.indexOf('detail-') === 0) primary = (await asFile(r.packshotFront, 'qa-ref-front')) || u.front || null;

    return [primary, ...closeups].filter((f): f is File => !!f);
}

interface AiFixResult {
    ok: boolean;                 // true when the image ends up passing QA
    alreadyOk: boolean;          // QA found nothing wrong — nothing was regenerated
    rounds: number;              // how many repair attempts were made
    improved: boolean;           // the kept image is a new one (not the original)
    before: QAResult | null;
    after: QAResult | null;
    problems: string[];          // what the fix was asked to repair
}

// slot: 'packshot-front' | 'packshot-back' | 'model-front' | 'model-back'
async function aiFixBilledeSlot(slot: string, opts: RegenOpts & { rounds?: number; qa?: QAResult | null } = {}): Promise<AiFixResult> {
    const RESULT_KEY: Record<string, string> = {
        'packshot-front': 'packshotFront', 'packshot-back': 'packshotBack',
        'model-front': 'modelFront', 'model-back': 'modelBack'
    };
    DETAIL_SHOTS.forEach(d => { RESULT_KEY[d.slot] = d.key; });
    const key = RESULT_KEY[slot];
    if (!key) throw new Error('Unknown slot: ' + slot);
    if (typeof evaluateGarment !== 'function') throw new Error('QA is not available — reload the page');

    const r: any = state.billede.results;
    const original: ImageResult | null = (r && r[key] && r[key].data) ? r[key] : null;
    if (!original) throw new Error('No image in this slot to check — load or generate it first');

    const refs = await qaRefsForSlot(slot);
    if (!refs.length) throw new Error('Missing the original photo to compare against — load the generation into the preview first');

    appendLog(`\n── AI-fix ${slot} — checking the image ──`);
    let before: QAResult | null = opts.qa || null;
    if (!before) before = await evaluateGarment(refs, original);
    if (!before) throw new Error('The quality check could not be run right now — try again');
    appendLog('  ' + qaSummary(slot, before));

    if (!qaBlocks(before)) {
        appendLog('  ✓ AI-fix: no faults found — the image is kept as it is');
        return { ok: true, alreadyOk: true, rounds: 0, improved: false, before, after: before, problems: [] };
    }

    const score = (q: QAResult | null) => q ? ((q.detail_quality_score ?? 0) + (q.product_fidelity_score ?? 0) + (q.color_preservation_score ?? 0)) : -1;
    let bestImg: ImageResult = original;
    let bestQa: QAResult | null = before;
    let bestScore = score(before);
    let latestQa: QAResult | null = before;
    let rounds = 0;
    const problems = [...(before.failure_reasons || []), ...(before.issues || [])].map(String);

    // Keep the slot's own look: packshot vs flat-lay follows the current output setting unless the
    // caller overrides it, so a fix never silently converts a packshot into a flat-lay.
    const style: 'packshot' | 'flat' | undefined = opts.style
        || (slot.indexOf('packshot-') === 0 ? (state.billede.outputStyle === 'flat' ? 'flat' : 'packshot') : undefined);

    const maxRounds = Math.max(1, Math.min(3, opts.rounds || AI_FIX_MAX_ROUNDS));
    for (let round = 1; round <= maxRounds; round++) {
        rounds = round;
        const instruction = appendUserInstruction(buildQaFixInstruction(latestQa), opts.instruction);
        appendLog(`  [AI-fix] attempt ${round}/${maxRounds} — repairing: ${(latestQa?.failure_reasons || latestQa?.issues || ['quality below threshold']).join('; ').substring(0, 160)}`);
        try {
            await regenerateBilledeSlot(slot, { style, model: opts.model, instruction });
        } catch (e) {
            appendLog(`  ⚠ [AI-fix] attempt ${round} failed: ${(e as Error).message || 'error'}`);
            continue;
        }
        const candidate: ImageResult | null = (r[key] && r[key].data) ? r[key] : null;
        if (!candidate) continue;
        const qa = await evaluateGarment(refs, candidate);
        latestQa = qa || latestQa;
        if (qa) appendLog('  ' + qaSummary(slot, qa));
        const s = score(qa);
        if (s > bestScore) { bestImg = candidate; bestQa = qa; bestScore = s; }
        if (qa && !qaBlocks(qa)) { bestImg = candidate; bestQa = qa; bestScore = s; break; }
    }

    // Put the winner back in the slot — that can be the ORIGINAL if every attempt scored worse.
    r[key] = bestImg;
    if (typeof displayOutputImage === 'function') displayOutputImage(slot, bestImg.data);
    const improved = bestImg !== original;
    const ok = !qaBlocks(bestQa);
    appendLog(improved
        ? `  ✓ AI-fix ${slot}: kept the repaired version${ok ? ' — QA now passes' : ' — QA still flags it, review manually'}`
        : `  ⚠ AI-fix ${slot}: no attempt beat the original — the original image is kept`);
    return { ok, alreadyOk: false, rounds, improved, before, after: bestQa, problems };
}

type PhaseFn = (ctx?: any) => Promise<void>;

const PHASE_FNS: Record<number, PhaseFn> = {
    1: phasePackshots,
    2: phaseDescription,
    3: phaseTextSeo,
    4: phaseModels,
    5: phaseDetails
};

const PHASE_LABELS: Record<number, string> = {
    1: 'Packshots',
    2: 'Description',
    3: 'Text & SEO',
    4: 'Model Images',
    5: 'Detail Shots'
};

async function rerunSinglePhase(phase: number): Promise<void> {
    const fn = PHASE_FNS[phase];
    if (!fn) return;

    const btn = document.getElementById('generateBtn') as HTMLButtonElement | null;
    if (btn && btn.disabled) {
        showToast('Pipeline is already running — wait for it to finish.', { type: 'warn' });
        return;
    }

    // Reset status for the phase
    const item = document.querySelector(`.stepper-item[data-phase="${phase}"]`);
    if (item) {
        item.classList.remove('completed', 'active');
        const icon = item.querySelector('.stepper-icon');
        if (icon) icon.textContent = String(phase);
    }

    if (btn) btn.disabled = true;
    const _logBox1 = document.getElementById('logBox');
    if (_logBox1) _logBox1.classList.add('visible');
    const logWrap = document.getElementById('billedeLogWrap');
    if (logWrap) { logWrap.classList.add('visible'); (logWrap as HTMLElement).style.display = ''; }
    document.querySelector('.status-dot')!.classList.remove('ready');
    document.getElementById('statusText')!.textContent = `Running ${PHASE_LABELS[phase] || 'phase ' + phase}...`;

    appendLog(`\n↻ Rerun phase ${phase}: ${PHASE_LABELS[phase] || ''}`);

    try {
        await discoverModels();
        await fn();
        if (phase === 4) await modelImagesTo4K(state.billede, appendLog);
        appendLog(`\n✓ Phase ${phase} done`);
        document.querySelector('.status-dot')!.classList.add('ready');
        document.getElementById('statusText')!.textContent = 'Done!';
        showToast(`${PHASE_LABELS[phase]} regenerated`, { type: 'success' });
    } catch (error) {
        appendLog(`\n❌ ${(error as Error).message}`);
        document.getElementById('statusText')!.textContent = 'Error occurred';
        showToast(`Error: ${(error as Error).message.substring(0, 80)}`, { type: 'error' });
        console.error(error);
    } finally {
        if (btn) btn.disabled = false;
    }
}

async function runPipeline(): Promise<void> {
    const styleNrInput = document.getElementById('styleNr') as HTMLInputElement;
    const colorCodeInput = document.getElementById('colorCode') as HTMLInputElement;
    const styleNr = styleNrInput.value;
    const colorCode = colorCodeInput.value;

    if (!styleNr || !colorCode) {
        if (typeof flagProductInfoErrors === 'function') flagProductInfoErrors();
        showToast('Fill in style number and color code before you start.', { type: 'warn', title: 'Product info missing' });
        return;
    }

    const phaseOrder: number[] = (typeof getEnabledPhasesInOrder === 'function')
        ? getEnabledPhasesInOrder()
        : [1, 2, 3, 4];
    // The detail shots are made from the model images, so they come after them
    // wherever the step was dragged to.
    if (phaseOrder.includes(5)) { phaseOrder.splice(phaseOrder.indexOf(5), 1); phaseOrder.push(5); }

    if (phaseOrder.length === 0) {
        showToast('Enable at least one phase in the pipeline to start.', { type: 'warn', title: 'Pipeline is empty' });
        return;
    }

    // Validate uploads for the phases that are actually selected
    if (phaseOrder.includes(1)) {
        if (!state.billede.uploads.front || !state.billede.uploads.back) {
            showToast('Upload both front and back images to run Packshots.', { type: 'warn', title: 'Images missing' });
            return;
        }
    } else if (phaseOrder.includes(2) && !state.billede.uploads.front) {
        showToast('Upload a front image to run Description.', { type: 'warn', title: 'Image missing' });
        return;
    }

    // --- BACKGROUND PIPELINE WORKFLOW ---
    // 1. Snapshot the job state
    const jobState = {
        id: 'pl_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 6),
        uploads: Object.assign({}, state.billede.uploads),
        results: {} as Record<string, any>,
        outputAspect: state.billede.outputAspect,
        outputResolution: state.billede.outputResolution,
        outputStyle: state.billede.outputStyle,
        outputModel: state.billede.outputModel,
        outputFileTypes: Array.isArray(state.billede.outputFileTypes) ? [...state.billede.outputFileTypes] : ['png'],
        qaGate: (state.billede as any).qaGate,
        bestOfN: (state.billede as any).bestOfN,
        modelBackground: (state.billede as any).modelBackground || 'white',   // 'white' | 'studio' — background for phase 4
        detailShots: Array.isArray((state.billede as any).detailShots) ? [...(state.billede as any).detailShots] : [...DEFAULT_DETAIL_SHOTS],
        model4k: (state.billede as any).model4k !== false,
        // a picture that fails its check is made again at once (selfFix)
        autoFix: true,
        qaPre: {} as Record<string, any>,
        onLog: (msg: string) => {},
        logs: [] as string[]
    };

    // Prepare uploads snapshot for Library
    const uploadsSnapshot: any = {};
    for (const k of ['front', 'back', 'detail', 'necktag', 'model']) {
        const f = jobState.uploads[k as keyof typeof jobState.uploads];
        if (f) {
            try {
                if (typeof fileToBase64 === 'function' && f instanceof File) {
                    const b64 = await fileToBase64(f);
                    uploadsSnapshot[k] = `data:${f.type || 'image/png'};base64,${b64}`;
                } else {
                    uploadsSnapshot[k] = f;
                }
            } catch (e) {
                uploadsSnapshot[k] = f;
            }
        }
    }

    // 2. Create the initial Library Entry (status: running)
    const entry = {
        id: jobState.id,
        styleNr: styleNr.trim(),
        colorCode: colorCode.trim(),
        created: Date.now(),
        updated: Date.now(),
        images: {},
        uploads: uploadsSnapshot,
        shopify: '', seo: '', jsonData: '',
        outputAspect: jobState.outputAspect || '',
        outputResolution: jobState.outputResolution || '',
        outputStyle: jobState.outputStyle || '',
        outputModel: jobState.outputModel || '',
        modelBackground: jobState.modelBackground || 'white',
        outputFileTypes: jobState.outputFileTypes || ['png'],
        variantCount: state.billede.variantCount || 4,
        phases: phaseOrder,
        status: 'running',
        stage: 'Queued...',
        logs: [] as string[]
    };

    jobState.onLog = (msg: string) => {
        const cleanMsg = msg.replace(/\n|──|✓|⚠/g, '').trim();
        if (cleanMsg) {
            entry.stage = cleanMsg;
            jobState.logs.push('[' + new Date().toLocaleTimeString('da-DK', {hour:'2-digit',minute:'2-digit',second:'2-digit'}) + '] ' + cleanMsg);
            entry.logs = jobState.logs;
            if (typeof savePackshotEntry === 'function') {
                savePackshotEntry(entry).then(() => {
                    if (typeof renderPackshotLibrary === 'function') renderPackshotLibrary();
                    if (typeof renderPackshotLibraryPopup === 'function') renderPackshotLibraryPopup();
                });
            }
        }
    };

    if (typeof savePackshotEntry === 'function') {
        await savePackshotEntry(entry);
        if (typeof renderPackshotLibrary === 'function') renderPackshotLibrary();
        if (typeof renderPackshotLibraryPopup === 'function') renderPackshotLibraryPopup();
    }

    showToast('Generation added to library queue.', { type: 'success' });

    // 3. Reset the UI for the next job
    styleNrInput.value = '';
    colorCodeInput.value = '';
    state.billede.uploads = { front: null, back: null, detail: null, necktag: null, model: null };
    state.billede.results = { packshotFront: null, packshotBack: null, description: null, shopify: null, seo: null, json: null, modelFront: null, modelBack: null };
    if (typeof updateFilenamePreview === 'function') updateFilenamePreview();
    
    // Clear DOM uploads
    document.querySelectorAll('.upload-zone').forEach(z => {
        z.classList.remove('has-file');
        const p = z.querySelector('.upload-zone-preview');
        if (p) p.innerHTML = '';
        const fname = z.querySelector('.upload-zone-filename') as HTMLElement;
        if (fname) {
            fname.style.display = 'none';
            fname.textContent = '';
        }
        const inp = z.querySelector('input[type="file"]') as HTMLInputElement;
        if (inp) inp.value = '';
    });
    
    // Clear UI state properly
    if (typeof window !== 'undefined' && typeof (window as any).clearBilledeUI === 'function') {
        (window as any).clearBilledeUI();
    }
    const stxt = document.getElementById('shopifyText'); if (stxt) stxt.textContent = '';
    const seotxt = document.getElementById('seoText'); if (seotxt) seotxt.textContent = '';
    const jtxt = document.getElementById('jsonText'); if (jtxt) jtxt.textContent = '';
    const txtRes = document.getElementById('textResults'); if (txtRes) txtRes.classList.remove('visible');

    // 4. Run the phases asynchronously in the background
    (async () => {
        let _hadError = false, _errMsg = '';
        // QA verdicts are collected while the job runs, but the STATUS is only decided in step 5.
        // Writing 'needs_review' onto the entry mid-run would hide the fact that the job is still
        // going: the library would show REVIEW, and the detail view would render the warning banner
        // for a job that has no images yet instead of the live pipeline console.
        let _needsReview = false;
        // Auto-QA is ON by default (set state.billede.qaGate = false to disable). Every produced
        // image is scored by the vision-QA inspector; packshot scores additionally gate the model
        // phase so a bad reference can't propagate. All QA is fail-open — a failed/blocked check
        // never throws and never silently discards images (they are kept for manual rating).
        const _qaOn = (state as any)?.billede?.qaGate !== false;
        (entry as any).qaBySlot = (entry as any).qaBySlot || {};
        const _u: any = jobState.uploads;
        const _closeups = !_qaOn ? [] : [_u.necktag && await brandLabelUsed(jobState) ? _u.necktag : null, _u.detail].filter((f: any): f is File => !!f);
        const _runQa = async (slotKey: string, refs: (File | null | undefined)[], out: ImageResult | null | undefined): Promise<QAResult | null> => {
            if (!_qaOn || !out || !(out as any).data) return null;
            // checked already while it was made (selfFix)
            const pre = (jobState as any).qaPre && (jobState as any).qaPre[slotKey];
            const qa = pre !== undefined ? pre : await evaluateGarment(refs.filter((f): f is File => !!f), out);
            if (qa) {
                (entry as any).qaBySlot[slotKey] = qa;
                jobState.onLog(qaSummary(slotKey, qa));
            }
            return qa;
        };
        try {
            await discoverModels();
            for (let i = 0; i < phaseOrder.length; i++) {
                const phaseNum = phaseOrder[i];
                const fn = PHASE_FNS[phaseNum];
                if (!fn) continue;
                await fn(jobState);

                // ── QA after packshots (doc §G.4) — FLAG ONLY, NEVER STOP.
                // This used to be a hard stop-gate that skipped the model phase on a bad packshot
                // verdict. It doesn't any more: if the user selected model images, the pipeline
                // always produces them. A failed QA only sets needs_review + gateReason so the
                // library shows the warning and the ✨ AI-fix / regenerate buttons can act on it.
                if (phaseNum === 1) {
                    const qaFront = await _runQa('packshot-front', [_u.front, ..._closeups], jobState.results.packshotFront as ImageResult);
                    const qaBack  = await _runQa('packshot-back',  [_u.back,  ..._closeups], jobState.results.packshotBack  as ImageResult);
                    (entry as any).qa = qaFront || (entry as any).qa; // back-compat single-field
                    if (qaBlocks(qaFront) || qaBlocks(qaBack)) {
                        _needsReview = true;
                        const reasons = [...((qaFront?.failure_reasons) || []), ...((qaBack?.failure_reasons) || [])];
                        (entry as any).gateReason = reasons.length ? reasons : ['Packshot QA below threshold'];
                        jobState.onLog('⚠ Packshot QA below threshold — marked needs_review. The pipeline CONTINUES (model images are still produced). ' + reasons.join('; '));
                    }
                }

                // ── QA the model images for the record + a needs_review flag (last phase, no break;
                // the images are kept so the user can rate/override them manually).
                if (phaseNum === 4) {
                    const pf = jobState.results.packshotFront as ImageResult | undefined;
                    const pb = jobState.results.packshotBack as ImageResult | undefined;
                    const mFrontRef = pf ? await base64ToFile(pf.data, pf.mime, 'qa-ref-front') : _u.front;
                    const mBackRef  = pb ? await base64ToFile(pb.data, pb.mime, 'qa-ref-back')  : _u.back;
                    const qaMF = await _runQa('model-front', [mFrontRef, ..._closeups], jobState.results.modelFront as ImageResult);
                    const qaMB = await _runQa('model-back',  [mBackRef,  ..._closeups], jobState.results.modelBack  as ImageResult);
                    if ((qaBlocks(qaMF) || qaBlocks(qaMB)) && !_needsReview) {
                        _needsReview = true;
                        const reasons = [...((qaMF?.failure_reasons) || []), ...((qaMB?.failure_reasons) || [])];
                        (entry as any).gateReason = reasons.length ? reasons : ['Model QA below threshold'];
                        jobState.onLog('⚠ Model QA below threshold — marked needs_review (images kept for manual rating).');
                    }
                }
            }
            // The model images in 4K, once everything made from them is done.
            await modelImagesTo4K(jobState, jobState.onLog);
        } catch (error) {
            _hadError = true;
            _errMsg = (error as Error)?.message || String(error);
            console.error('Background pipeline error:', error);
            entry.status = 'failed';
        }

        // 5. Save the final results to the Library Entry.
        // Harvest the produced images UNCONDITIONALLY: if a late phase threw, everything the
        // earlier phases finished (typically the packshots) must still land in the library instead
        // of disappearing with the failed job.
        const finalImages: any = {};
        const imageMeta: any = {};
        const madeBySlot: Record<string, any> = {};
        for (const slot of ['packshotFront', 'packshotBack', 'modelFront', 'modelBack', ...DETAIL_SHOTS.map(d => d.key)]) {
            const r = jobState.results[slot as keyof typeof jobState.results];
            if (r && (r as any).data) {
                const slotKey = slot.replace(/([A-Z])/g, '-$1').toLowerCase();
                finalImages[slotKey] = `data:${(r as any).mime || 'image/png'};base64,${(r as any).data}`;
                imageMeta[slotKey] = { engine: (r as any).engine || null, model: (r as any).model || null };
                madeBySlot[slotKey] = r;
            }
        }
        entry.images = finalImages;
        (entry as any).imageMeta = imageMeta;
        entry.shopify = jobState.results.shopify || '';
        entry.seo = jobState.results.seo || '';
        entry.jsonData = jobState.results.json || '';
        entry.updated = Date.now();
        // The status is decided HERE, once, at the end of the run — never mid-flight.
        entry.status = _hadError ? 'failed' : (_needsReview ? 'needs_review' : 'done');
        actReportRun(entry, finalImages, imageMeta, jobState, _hadError ? (_errMsg || 'The run failed') : '', madeBySlot);

        if (!_hadError) {
            // Prompt versioning: record which prompt versions produced this job (best-effort).
            let promptVersions: Record<string, number> = {};
            try {
                const _vr = await apiFetch('/api/prompts/active-versions');
                if (_vr.ok) { promptVersions = await _vr.json(); (entry as any).promptVersions = promptVersions; }
            } catch { /* fail-open */ }

            // Persist one metadata record per produced output (doc §G.1 / §8). Fail-open.
            const flowStepFor = (slotKey: string) => slotKey.startsWith('packshot') ? 'floor_to_packshot'
                : slotKey.startsWith('detail') ? 'model_to_detail' : 'packshot_to_model';
            const promptKeyFor: Record<string, string> = {
                'packshot-front': 'PACKSHOT', 'packshot-back': 'PACKSHOT',
                'model-front': 'MODEL', 'model-back': 'MODEL'
            };
            DETAIL_SHOTS.forEach(d => { promptKeyFor[d.slot] = 'MODEL_DETAIL'; });
            await Promise.all(Object.keys(finalImages).map((slotKey) => {
                const meta = imageMeta[slotKey] || {};
                const qa = (entry as any).qaBySlot[slotKey] || null;
                const promptKey = promptKeyFor[slotKey] || '';
                // AI-metadata export (Boozt): gør provenance tilgængelig for download-funnelen
                // (creator.html → exportAssetViaServer), så /api/export/image kan slå
                // generation-recorden op og skrive korrekte IPTC/C2PA-felter. Fail-open.
                try {
                    const w = window as any;
                    w.billedeExportMeta = w.billedeExportMeta || {};
                    w.billedeExportMeta[slotKey] = {
                        generation_id: entry.id + '__' + slotKey,
                        model_name: meta.model || null,
                        engine: meta.engine || null
                    };
                } catch { /* fail-open — provenance er kun et opslag */ }
                return postGenerationMeta({
                    generation_id: entry.id + '__' + slotKey,
                    parent_generation_id: entry.id,
                    product_id: (entry.styleNr || '') + (entry.colorCode ? '-' + entry.colorCode : ''),
                    flow_step: flowStepFor(slotKey),
                    slot: slotKey,
                    prompt_key: promptKey,
                    prompt_version: promptKey ? (promptVersions[promptKey] ?? null) : null,
                    model_name: meta.model || null,
                    model_settings: {
                        engine: meta.engine || null,
                        aspect: entry.outputAspect || null,
                        resolution: entry.outputResolution || null,
                        style: entry.outputStyle || null,
                        outputModel: entry.outputModel || null
                    },
                    auto_quality_score: qa?.product_fidelity_score ?? null,
                    detail_quality_score: qa?.detail_quality_score ?? null,
                    product_fidelity_score: qa?.product_fidelity_score ?? null,
                    color_preservation_score: qa?.color_preservation_score ?? null,
                    failure_reasons: qa?.failure_reasons || [],
                    recommended_action: qa?.recommended_action || null,
                    status: entry.status,
                    created_at: entry.created
                });
            }));
        }

        if (typeof savePackshotEntry === 'function') {
            await savePackshotEntry(entry);
            if (typeof renderPackshotLibrary === 'function') renderPackshotLibrary();
            if (typeof renderPackshotLibraryPopup === 'function') renderPackshotLibraryPopup();
        }
        
        if (!_hadError) {
            showToast(`Completed: ${styleNr} ${colorCode}`, { type: 'success' });
        } else {
            showToast(`Failed: ${styleNr} ${colorCode}`, { type: 'error' });
        }
    })();
}

// What a Packshot run made, and why it failed or was stopped, for the admin page
// (2026-10-20, src/admin.ts): each picture with the photo it was made from and its
// automatic check. Sent after the run, in the background; never in its way.
// what the automatic fix is told of a picture (src/admin.ts AutoInfo), and its close look
async function autoOf(r: any): Promise<{ auto: any; assessId?: string }> {
    const m: MadeWith = (r && r.made) || {};
    let promptVersion: number | null = null;
    if (m.promptKey) { try { const v = await (await apiFetch('/api/prompts/active-versions')).json(); promptVersion = v[m.promptKey] ?? null; } catch { /* unknown */ } }
    const f = r && r.firstLook;
    const first = f ? { verdict: f.verdict, score: f.score, fix: f.fix_prompt || '', defects: (f.defects || []).slice(0, 8).map((d: any) => ({ severity: d.severity, title: d.title, detail: d.detail })) } : undefined;
    return { auto: { selfFix: r && r.selfFix, kind: m.kind, promptKey: m.promptKey, promptVersion, refIndex: m.refIndex, aspect: m.aspect || (state as any)?.billede?.outputAspect, resolution: m.resolution || (state as any)?.billede?.outputResolution, fields: m.fields, first },
        assessId: r && r.look ? r.look.id : undefined };
}
// a picture reported; its photos sent as well when the automatic fix wants them
async function reportMade(body: any, r: any): Promise<void> {
    const act = (window as any).ffActivity;
    if (!act) return;
    const j = await act.image(body);
    const inputs: File[] = (r && r.made && r.made.inputs) || [];
    if (j && j.wantInputs && j.id && inputs.length) {
        try {
            const list = [];
            for (const f of inputs) list.push(await fileDataUrl(f, 1600));
            const up = await apiFetch('/api/activity/' + encodeURIComponent(j.id) + '/inputs', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ inputs: list }) });
            if (up.ok) await apiFetch('/api/activity/' + encodeURIComponent(j.id) + '/inputs/done', { method: 'POST' });
        } catch { /* the next one */ }
    }
}

function actReportRun(entry: any, images: Record<string, string>, meta: Record<string, any>, job: any, failed: string, made: Record<string, any> = {}) {
    const act = (window as any).ffActivity;
    if (!act) return;
    (async () => {
        const u = job.uploads || {};
        const product = [entry.styleNr, entry.colorCode].filter(Boolean).join(' · ');
        const settings = { style: entry.outputStyle, aspect: entry.outputAspect, resolution: entry.outputResolution, engine: entry.outputModel };
        for (const [slot, src] of Object.entries(images)) {
            const q = entry.qaBySlot && entry.qaBySlot[slot];
            const num = (x: unknown) => (typeof x === 'number' && isFinite(x) ? x : null);
            const fid = num(q && q.product_fidelity_score), det = num(q && q.detail_quality_score);
            const refSrc = slot === 'packshot-front' ? u.front : slot === 'packshot-back' ? u.back
                : (/back/.test(slot) ? images['packshot-back'] || u.back : images['packshot-front'] || u.front);
            await reportMade({
                tool: 'packshot', src, slot, product, model: meta[slot]?.model, engine: meta[slot]?.engine, settings, ref: entry.id, refSrc,
                qa: q ? { score: fid != null && det != null ? Math.round((fid + det) / 2) / 10 : null, fidelity: fid, detail: det, colour: num(q.color_preservation_score),
                    issues: [...(q.issues || []), ...(q.failure_reasons || [])].slice(0, 12), action: q.recommended_action || null } : undefined,
                ...(await autoOf(made[slot])),
            }, made[slot]);
        }
        if (failed) act.error({ tool: 'packshot', message: failed, product, ref: entry.id });
        else if (entry.status === 'needs_review' && Array.isArray(entry.gateReason) && entry.gateReason.length) {
            act.error({ tool: 'packshot', code: 'QA_GATE', message: 'Stopped for review: ' + entry.gateReason.join('; '), product, ref: entry.id });
        }
    })().catch(() => undefined);
}

// The problems from before the admin page (2026-10-20, Simon: "vi kunne ikke få de ting ind
// som faktisk er et problem"): the runs in this browser's Packshot library, started before the
// page went live, that failed, stopped for review, or made a picture its check failed (the
// stop-gate's own limits), are sent once, with the pictures and the photos they were made
// from. Each is sent under its run and slot, so sending it again changes nothing, and the
// server's own record of the same picture gets its picture and its person. Once all are in,
// the library is not read for it again.
const ACT_LIVE_SINCE = Date.UTC(2026, 9, 20, 9, 20);       // = LIVE_SINCE in src/admin.ts
const ACT_OLD_KEY = 'ffActOld1';
function actQaFails(q: any): boolean {
    if (!q) return false;
    const a = String(q.recommended_action || '').toLowerCase();
    return a === 'reject' || a === 'regenerate'
        || (typeof q.detail_quality_score === 'number' && q.detail_quality_score < QA_DETAIL_MIN)
        || (typeof q.product_fidelity_score === 'number' && q.product_fidelity_score < QA_FIDELITY_MIN);
}
async function actBringInLibrary(): Promise<void> {
    const act = (window as any).ffActivity;
    if (!act || typeof act.old !== 'function') return;
    let state: { all?: boolean; sent?: string[] } = {};
    try { state = JSON.parse(localStorage.getItem(ACT_OLD_KEY) || '{}') || {}; } catch { state = {}; }
    if (state.all) return;
    const sent = new Set(state.sent || []);
    const keep = (all: boolean) => { try { localStorage.setItem(ACT_OLD_KEY, JSON.stringify(all ? { all: true } : { sent: [...sent] })); } catch { /* kept next time */ } };
    const oldest = Date.now() - 180 * 86400_000;
    const metas = await getPackshotLibraryLight();
    const todo = (metas || []).filter((m: any) => m && m.id && !sent.has(m.id) && m.status !== 'running'
        && (m.created || 0) > oldest && (m.created || 0) < ACT_LIVE_SINCE
        && (m.status === 'failed' || m.status === 'needs_review' || Object.values(m.qaBySlot || {}).some(actQaFails)))
        .sort((a: any, b: any) => (b.created || 0) - (a.created || 0));
    let n = 0, left = false;
    for (const m of todo) {
        if (n >= 300) { left = true; break; }                // the rest the next time Creator is opened
        const e = await getPackshotEntry(m.id);
        if (!e) { sent.add(m.id); continue; }
        const u = e.uploads || {}, images = e.images || {}, qaBy = e.qaBySlot || {};
        const product = [e.styleNr, e.colorCode].filter(Boolean).join(' · ');
        const settings = { style: e.outputStyle, aspect: e.outputAspect, resolution: e.outputResolution, engine: e.outputModel };
        const things: any[] = [];
        for (const [slot, src] of Object.entries(images) as [string, string][]) {
            const q = qaBy[slot];
            if (!src || !actQaFails(q)) continue;
            const num = (x: unknown) => (typeof x === 'number' && isFinite(x) ? x : null);
            const fid = num(q.product_fidelity_score), det = num(q.detail_quality_score);
            things.push({
                kind: 'image', tool: 'packshot', key: 'gen:' + e.id + '__' + slot, at: e.created, src, slot, product, settings, ref: e.id,
                model: e.imageMeta?.[slot]?.model, engine: e.imageMeta?.[slot]?.engine,
                refSrc: slot === 'packshot-front' ? u.front : slot === 'packshot-back' ? u.back : (/back/.test(slot) ? images['packshot-back'] || u.back : images['packshot-front'] || u.front),
                qa: { score: fid != null && det != null ? Math.round((fid + det) / 2) / 10 : null, fidelity: fid, detail: det, colour: num(q.color_preservation_score),
                    issues: [...(q.issues || []), ...(q.failure_reasons || [])].slice(0, 12), action: q.recommended_action || null },
            });
        }
        if (e.status === 'failed') {
            const logs: string[] = Array.isArray(e.logs) ? e.logs : [];
            const said = [...logs].reverse().find(l => /fail|error|fejl|stopped|abort/i.test(l)) || e.stage || 'The run failed';
            things.push({ kind: 'error', tool: 'packshot', key: 'run:' + e.id, at: e.created, message: String(said).replace(/^\[[^\]]*\]\s*/, ''), product, ref: e.id });
        } else if (e.status === 'needs_review') {
            const why = Array.isArray(e.gateReason) && e.gateReason.length ? e.gateReason.join('; ') : 'the quality check';
            things.push({ kind: 'error', tool: 'packshot', code: 'QA_GATE', key: 'gate:' + e.id, at: e.created, message: 'Stopped for review: ' + why, product, ref: e.id });
        }
        let ok = true;
        for (const t of things) {
            const r = await act.old(t);
            if (r === 'stop') { keep(false); return; }          // too many at once: on the next open
            if (!r) { ok = false; break; }
            n++;
            await new Promise(res => setTimeout(res, 150));
        }
        if (!ok) { left = true; continue; }
        sent.add(m.id);
        keep(false);
    }
    keep(!left);
}


// ── Pictures made again in the person's library ─────────────
// An administrator asks on the admin page for a Packshot picture to be made again in the
// person's library (2026-10-20, Simon: "så vi kunne sætte de billeder og erstat når det er
// fixet ... ind i personens bibliotek. Det skal virke okay"). The library and its photos at
// full size are only in the person's own browser, so it is made there, the next time they
// open Creator: in the background, from the entry's own photos and settings and with today's
// Packshot, without touching what they are working on. The new picture takes the old one's
// place in the entry (the old one is kept in entry.replaced), and comes back to the admin
// page with its check.
const LIB_KEY: Record<string, string> = { 'packshot-front': 'packshotFront', 'packshot-back': 'packshotBack', 'model-front': 'modelFront', 'model-back': 'modelBack' };
DETAIL_SHOTS.forEach(d => { LIB_KEY[d.slot] = d.key; });
const dataUrlFile = async (src: unknown, name: string): Promise<File | null> => {
    if (typeof src !== 'string' || !src.startsWith('data:')) return null;
    return await base64ToFile(src.slice(src.indexOf(',') + 1), src.slice(5, src.indexOf(';')) || 'image/png', name);
};
const dataUrlResult = (src: unknown): ImageResult | null => typeof src === 'string' && src.startsWith('data:')
    ? { data: src.slice(src.indexOf(',') + 1), mime: src.slice(5, src.indexOf(';')) || 'image/png' } as ImageResult : null;
const longSideOf = (src: unknown) => new Promise<number>(ok => {
    if (typeof src !== 'string') return ok(0);
    const i = new Image(); i.onload = () => ok(Math.max(i.naturalWidth, i.naturalHeight)); i.onerror = () => ok(0); i.src = src;
});

// a generation's ctx from a library entry, as the run that made it had it
async function libraryCtx(e: any, log: (m: string) => void): Promise<any> {
    const up = e.uploads || {};
    const uploads: any = {};
    for (const k of ['front', 'back', 'detail', 'necktag', 'model']) uploads[k] = await dataUrlFile(up[k], k + '.jpg');
    // the brand's label or the garment's own, it is not told apart in the library: the look decides
    if (uploads.necktag) (uploads.necktag as any).__brandTag = 'library';
    const results: any = {};
    for (const [slot, key] of Object.entries(LIB_KEY)) { const r = dataUrlResult(e.images && e.images[slot]); if (r) results[key] = r; }
    return { uploads, results, outputAspect: e.outputAspect || '1:1', outputResolution: e.outputResolution || '2k', outputStyle: e.outputStyle || 'packshot',
        outputModel: e.outputModel || 'gemini', modelBackground: e.modelBackground || 'white', detailShots: e.detailShots, onLog: log };
}

// one picture of a library entry made again: the new picture and its check
async function remakeLibrarySlot(e: any, slot: string, instruction: string, log: (m: string) => void): Promise<{ res: ImageResult; qa: QAResult | null }> {
    const ctx = await libraryCtx(e, log);
    const shape: PictureShape = { aspect: ctx.outputAspect, resolution: ctx.outputResolution, background: e.packshotBackground || 'white' };
    const isFlat = ctx.outputStyle === 'flat';
    const preferOpenAi = ctx.outputModel === 'openai';
    const u = ctx.uploads, r = ctx.results;
    sameGarment(u.back, u.front);
    let res: ImageResult;
    ctx.autoFix = true;
    if (slot === 'packshot-front') {
        if (!u.front) throw new Error('The entry has no front photo');
        const closeups = await closeupsFor(ctx, log);
        res = await buildFrontPackshot(u.front, isFlat, preferOpenAi, instruction, log, closeups, shape);
        res = await selfFix(ctx, slot, res, [u.front, ...closeups], ins => buildFrontPackshot(u.front, isFlat, preferOpenAi, [instruction, ins].filter(Boolean).join(' '), log, closeups, shape), log);
    } else if (slot === 'packshot-back') {
        if (!u.back) throw new Error('The entry has no back photo');
        const closeups = detailRefImgs(ctx);
        res = await buildBackPackshot(u.back, isFlat, preferOpenAi, instruction, r.packshotFront || null, log, closeups, shape);
        res = await selfFix(ctx, slot, res, [u.back, ...closeups.filter(f => (f as any).__closeup !== 'necktag')], ins => buildBackPackshot(u.back, isFlat, preferOpenAi, [instruction, ins].filter(Boolean).join(' '), r.packshotFront || null, log, closeups, shape), log);
    } else if (slot === 'model-front' || slot === 'model-back') {
        const worn = wornCloseups(ctx);
        const choices = modelChoices(ctx);
        const opts = { preferOpenAi, onLog: log, promptAsIs: true, ...shapeOpts(shape) };
        if (slot === 'model-front') {
            const ref = r.packshotFront ? await base64ToFile(r.packshotFront.data, r.packshotFront.mime, 'front-ps') : u.front;
            if (!ref) throw new Error('The entry has no front packshot or photo');
            const prompt = appendUserInstruction(buildModelPrompt({ view: 'front', person: u.model ? 'upload' : 'generate', withFront: false, fromPackshot: !!r.packshotFront, closeups: worn, ...choices }), instruction);
            res = await geminiImage(prompt, u.model ? [u.model, ref, ...worn] : [ref, ...worn], opts);
        } else {
            const ref = r.packshotBack ? await base64ToFile(r.packshotBack.data, r.packshotBack.mime, 'back-ps') : (u.back || u.front);
            if (!ref) throw new Error('The entry has no back packshot or photo');
            if (r.modelFront) {
                const fm = await base64ToFile(r.modelFront.data, r.modelFront.mime, 'model-front-ref');
                const prompt = appendUserInstruction(buildModelPrompt({ view: 'back', person: u.model ? 'upload' : 'generate', withFront: true, fromPackshot: !!r.packshotBack, closeups: worn, ...choices }), instruction);
                res = await geminiImage(prompt, [fm, ref, ...worn], opts);
            } else {
                const prompt = appendUserInstruction(buildModelPrompt({ view: 'back', person: 'generate', withFront: false, fromPackshot: !!r.packshotBack, closeups: worn, ...choices }), instruction);
                res = await geminiImage(prompt, [ref, ...worn], opts);
            }
        }
        res = await fitToShape(await cleanWhiteBackground(res), shape);
        // 4K when the picture it replaces was
        if (await longSideOf(e.images && e.images[slot]) > 2800) res = (await modelImageTo4K(res, slot.replace('-', ' '), log)) || res;
    } else {
        const d = DETAIL_SHOTS.find(x => x.slot === slot);
        if (!d) throw new Error('This picture cannot be made again: ' + slot);
        res = await buildDetailShot(d, ctx, instruction, log, shape);
    }
    // its check, against what it was made from, as in the run (made already when it fixed itself)
    ctx.results[LIB_KEY[slot]] = res;
    const qa = (res as any).qa !== undefined ? (res as any).qa : await evaluateGarment(await qaRefsForSlot(slot, ctx), res).catch(() => null);
    return { res, qa };
}

// the new picture in the entry, in place of the old one (read again: it may have changed meanwhile)
async function putIntoLibrary(fix: any, res: ImageResult, qa: QAResult | null): Promise<any> {
    const e = await getPackshotEntry(fix.entry);
    if (!e) throw new Error('The entry is no longer in the library');
    const slot = fix.slot;
    const src = 'data:' + (res.mime || 'image/png') + ';base64,' + res.data;
    e.replaced = e.replaced || {};
    if (e.images && e.images[slot]) e.replaced[slot] = { image: e.images[slot], at: Date.now(), by: fix.by, qa: (e.qaBySlot && e.qaBySlot[slot]) || null };
    e.images = Object.assign({}, e.images, { [slot]: src });
    if (qa) e.qaBySlot = Object.assign({}, e.qaBySlot, { [slot]: qa });
    e.imageMeta = Object.assign({}, e.imageMeta, { [slot]: { model: (res as any).model || null, engine: (res as any).engine || null } });
    if (e.__cutSlots) delete e.__cutSlots[slot];          // the new packshot is cut out when it is saved
    e.fixed = Object.assign({}, e.fixed, { [slot]: { at: Date.now(), by: fix.by, fix: fix.id } });
    // a run stopped for review whose pictures all pass now is done
    if (e.status === 'needs_review' && Object.values(e.qaBySlot || {}).every(q => !actQaFails(q))) e.status = 'done';
    await savePackshotEntry(e);
    const meta = (e.imageMeta && e.imageMeta[slot]) || {};
    postGenerationMeta({ generation_id: e.id + '__' + slot, parent_generation_id: e.id, product_id: (e.styleNr || '') + (e.colorCode ? '-' + e.colorCode : ''), slot,
        model_name: meta.model || null, detail_quality_score: qa?.detail_quality_score ?? null, product_fidelity_score: qa?.product_fidelity_score ?? null,
        color_preservation_score: qa?.color_preservation_score ?? null, failure_reasons: qa?.failure_reasons || [], recommended_action: qa?.recommended_action || null,
        status: e.status, created_at: e.created });
    // the same entry open in Packshot: the new picture there too, so a later save keeps it
    if ((state as any).__libraryEntryId === e.id && LIB_KEY[slot]) {
        (state.billede.results as any)[LIB_KEY[slot]] = res;
        if (typeof displayOutputImage === 'function') displayOutputImage(slot, res.data);
    }
    if (typeof renderPackshotLibrary === 'function') renderPackshotLibrary();
    if (typeof renderPackshotLibraryPopup === 'function') renderPackshotLibraryPopup();
    return e;
}

let __fixing = false, __fixLast = 0;
const nameOf = (u: string) => String(u || '').split('@')[0].replace(/[._-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
async function applyLibraryFixes(force = false): Promise<number> {
    if (__fixing || typeof apiFetch !== 'function') return 0;
    if (!force && Date.now() - __fixLast < 120_000) return 0;
    __fixing = true;
    __fixLast = Date.now();
    try {
        const r = await apiFetch('/api/library-fixes');
        if (!r.ok) return 0;
        const fixes: any[] = ((await r.json()) || {}).fixes || [];
        let made = 0, told = false;
        const done: string[] = [];
        for (const f of fixes) {
            const tell = (status: string, error?: string, cleared?: boolean) => apiFetch('/api/library-fixes/' + encodeURIComponent(f.id), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status, error, cleared }) });
            const e = await getPackshotEntry(f.entry);
            if (!e) { await tell('missing').catch(() => undefined); continue; }
            // made by the server already (2026-10-22): only put in the library, in place of the old one
            if (f.deliver) {
                try {
                    const pr = await apiFetch('/api/library-fixes/' + encodeURIComponent(f.id) + '/picture');
                    if (!pr.ok) continue;
                    const { image, review } = await pr.json();
                    const res = dataUrlResult(image);
                    if (!res) continue;
                    // the close look stands in for the check it was made with
                    const sc = typeof review?.score === 'number' ? Math.round(review.score * 10) : null;
                    const qa: any = review ? { product_fidelity_score: sc, detail_quality_score: sc, issues: [], failure_reasons: [], recommended_action: review.verdict === 'fail' ? 'regenerate' : 'approve' } : null;
                    const after = await putIntoLibrary(f, res, qa);
                    await tell('delivered', undefined, after.status === 'done');
                    made++;
                    done.push(([e.styleNr, e.colorCode].filter(Boolean).join(' · ') || 'A picture') + ' · ' + f.slot.replace('-', ' '));
                } catch { /* the next time */ }
                continue;
            }
            const claim = await tell('working');
            if (!claim.ok) continue;
            const product = [e.styleNr, e.colorCode].filter(Boolean).join(' · ');
            if (!told) {
                told = true;
                showToast(nameOf(f.by) + ' asked for pictures in your library to be made again. They are being made now, from your own photos; you can keep working.', { type: 'info', title: 'Your library' });
            }
            try {
                const { res, qa } = await remakeLibrarySlot(e, f.slot, f.instruction || '', () => undefined);
                const after = await putIntoLibrary(f, res, qa);
                // back to the admin page, with its check: that is what says it is done
                const num = (x: unknown) => (typeof x === 'number' && isFinite(x) ? x : null);
                const fid = num(qa?.product_fidelity_score), det = num(qa?.detail_quality_score);
                const u = e.uploads || {};
                await reportMade({ ...(await autoOf(res)), cleared: after.status === 'done', tool: 'packshot', src: 'data:' + (res.mime || 'image/png') + ';base64,' + res.data, slot: f.slot, product, model: (res as any).model, engine: (res as any).engine,
                    refSrc: f.slot === 'packshot-back' ? u.back : f.slot === 'packshot-front' ? u.front : (/back/.test(f.slot) ? (e.images || {})['packshot-back'] : (e.images || {})['packshot-front']),
                    settings: { style: e.outputStyle, aspect: e.outputAspect, resolution: e.outputResolution, engine: e.outputModel, made: 'again, in the library' },
                    qa: qa ? { score: fid != null && det != null ? Math.round((fid + det) / 2) / 10 : null, fidelity: fid, detail: det, colour: num(qa.color_preservation_score),
                        issues: [...(qa.issues || []), ...(qa.failure_reasons || [])].slice(0, 12), action: qa.recommended_action || null } : undefined,
                    prompt: f.instruction ? 'Made again in the library, with: ' + f.instruction : undefined, ref: 'fix:' + f.id }, res);
                made++;
                done.push((product || 'A picture') + ' · ' + f.slot.replace('-', ' '));
            } catch (err) {
                await tell('failed', String((err as Error)?.message || err).slice(0, 400)).catch(() => undefined);
            }
        }
        if (made) showToast(made === 1 ? done[0] + ' is made again in your library.' : made + ' pictures are made again in your library: ' + done.join(', ') + '.', { type: 'success', title: 'Your library' });
        return made;
    } catch { return 0; }
    finally { __fixing = false; }
}

// ── Campaign Generator Pipeline ──────────────────────────────

